const DEFAULT_PROXY_BASE = '/apps/gifting';
const CAMPAIGN_CODE_STORAGE_KEY = 'gifting_campaign_code';

/** @typedef {'token' | 'code' | ''} GiftingAccessMode */

/** @typedef {{ name: string, firstName?: string, lastName?: string, email?: string, phone?: string, igHandle?: string }} GiftingInfluencer */
/** @typedef {{ id: number, label: string, required?: boolean }} GiftingDeliverable */
/** @typedef {{ id: number, name: string, campaignType?: string, requiresRsvp?: boolean, requiresDeliverables?: boolean, minItemAllowance: number, maxItemAllowance: number, hideSoldOutProducts?: boolean, countByOutfit?: boolean, exactSelectionCount?: number, contentDeadline?: string, contentDeadlineNote?: string, briefHtml?: string, agreementHtml?: string, termsHtml?: string, contentWaiverHtml?: string, eventDate?: string, eventTime?: string, eventLocation?: string, rsvpDeadline?: string, deliverables?: GiftingDeliverable[] }} GiftingCampaign */
/** @typedef {{ productId: number, scheduleItemId: number, sizeId: number, qty: number, productName?: string, sizeName?: string, sku?: string, canReselect?: boolean }} GiftingExistingLine */
/** @typedef {{ sizeId: number, name: string, stockAvailable: number, isEnabledForPreference?: boolean, isScheduledStock?: boolean, hasEventStock?: boolean, eventStockAllocated?: number, eventStockRemaining?: number }} GiftingSize */
/** @typedef {{ lineId: number, dropId?: number, productId: number, scheduleItemId: number, isPendingProduct: boolean, name: string, sku?: string, thumbUrl?: string, dropName?: string, goLiveDate?: string, expectedShipFrom?: string, maxQtyPerLine?: number | null, productMaxQty?: number | null, productRequestedQty?: number | null, productRemainingQty?: number | null, totalStock?: number, stockBadge?: string, isAvailable?: boolean, isEventStockControlled?: boolean, setId?: number, setName?: string, sortOrder?: number, dropSortOrder?: number, listIndex?: number, sizes: GiftingSize[] }} GiftingProduct */
/** @typedef {{ dropId: number, name: string, expectedShipFrom?: string, goLiveDate?: string, sortOrder?: number }} GiftingDrop */
/** @typedef {{ assetId: number, assetType: string, title?: string, url?: string, urlUs?: string, isHero?: boolean, sortOrder?: number }} GiftingAsset */
/** @typedef {{ influencer: GiftingInfluencer, campaign: GiftingCampaign, products: GiftingProduct[], assets: GiftingAsset[], drops?: GiftingDrop[], status: string, canSubmit: boolean, requiresIdentity?: boolean, sessionToken?: string, changesRequestedMessage?: string, existingLines?: GiftingExistingLine[] }} GiftingSession */

/** @param {Record<string, unknown> | null | undefined} obj @param {...string} keys */
function pick(obj, ...keys) {
  if (!obj) return undefined;

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }

  return undefined;
}

/** @param {unknown} value @param {boolean} [defaultValue] */
function normalizeBoolean(value, defaultValue = false) {
  if (value === true || value === 1 || value === '1' || value === 'true' || value === 'True') return true;
  if (value === false || value === 0 || value === '0' || value === 'false' || value === 'False') return false;
  if (value === undefined || value === null || value === '') return defaultValue;
  return Boolean(value);
}

/** @param {unknown} data */
function isSessionPayloadValid(data) {
  const sessionRaw = pick(data, 'Session', 'session');
  const hasSession = Boolean(sessionRaw && typeof sessionRaw === 'object');
  const outcome = normalizeSessionStatus(pick(data, 'Outcome', 'outcome'));

  if (outcome === 'valid' || outcome === 'success') {
    return hasSession;
  }

  if (
    outcome === 'invalid' ||
    outcome === 'error' ||
    outcome === 'failed' ||
    outcome === 'expired' ||
    outcome === 'pending'
  ) {
    return false;
  }

  const validFlag = pick(data, 'Valid', 'valid');
  if (validFlag !== undefined && validFlag !== null && validFlag !== '') {
    return normalizeBoolean(validFlag, false) && hasSession;
  }

  return normalizeBoolean(pick(data, 'Ok', 'ok'), false) && hasSession;
}

/** @param {string} message */
function getInviteErrorPresentation(message) {
  const text = String(message || '').trim();
  const lower = text.toLowerCase();

  if (lower.includes('not been sent') || lower.includes('not sent yet')) {
    return {
      title: 'Invite not sent yet',
      message:
        text ||
        'This invite has not been sent yet. Ask the Sabo team to send your invite from the campaign admin before using this link.',
    };
  }

  if (lower.includes('expired')) {
    return {
      title: 'This link has expired',
      message: text || 'This invite link has expired. Contact the Sabo team for a new link.',
    };
  }

  if (lower.includes('declined')) {
    return {
      title: 'Invite unavailable',
      message: text || 'This gifting invite is no longer available.',
    };
  }

  return {
    title: 'Invalid invite link',
    message: text || 'This invite is not available.',
  };
}

/** @param {unknown} value */
function normalizeSessionStatus(value) {
  return String(value || '').trim().toLowerCase();
}

/** @param {Record<string, unknown>} record */
function resolveSessionStatus(record) {
  const influencerRaw = pick(record, 'Influencer', 'influencer');
  const influencer =
    influencerRaw && typeof influencerRaw === 'object'
      ? /** @type {Record<string, unknown>} */ (influencerRaw)
      : {};

  /** @type {string[]} */
  const candidates = [
    pick(record, 'Status', 'status'),
    pick(record, 'InfluencerStatus', 'influencerStatus'),
    pick(influencer, 'Status', 'status'),
  ]
    .map(normalizeSessionStatus)
    .filter(Boolean);

  const activeStatus = candidates.find(
    (status) =>
      status === 'changesrequested' || status === 'invited' || status === 'opened' || status === 'pending'
  );
  if (activeStatus) return activeStatus;

  return candidates[0] || '';
}

/** @param {GiftingSession} session */
function shouldShowGiftingWizard(session) {
  const status = normalizeSessionStatus(session.status);

  if (status === 'declined' || status === 'submitted') return false;

  if (isTokenAccessMode() || isCodeAccessMode()) {
    return true;
  }

  if (normalizeBoolean(session.canSubmit, true)) return true;
  if (status === 'invited' || status === 'opened') return true;

  if ((status === 'approved' || status === 'pending') && (session.products?.length || 0) > 0) {
    return true;
  }

  return false;
}

/** @param {unknown} value */
function normalizeDeliverables(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;

      const item = /** @type {Record<string, unknown>} */ (entry);
      const id = Number(pick(item, 'DeliverableId', 'deliverableId', 'Id', 'id') || 0);
      const label = String(pick(item, 'Label', 'label') || '').trim();

      if (!id || !label) return null;

      return {
        id,
        label,
        required: Boolean(pick(item, 'IsRequired', 'isRequired', 'Required', 'required')),
      };
    })
    .filter(Boolean);
}

/** @param {unknown} value @param {number} [fallback] */
function normalizeSortOrder(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** @param {Pick<GiftingProduct, 'sortOrder' | 'listIndex'>} a @param {Pick<GiftingProduct, 'sortOrder' | 'listIndex'>} b */
function compareBySortOrder(a, b) {
  const sortDiff = normalizeSortOrder(a.sortOrder) - normalizeSortOrder(b.sortOrder);
  if (sortDiff !== 0) return sortDiff;
  return normalizeSortOrder(a.listIndex) - normalizeSortOrder(b.listIndex);
}

/** @param {unknown} value */
function normalizeProducts(value) {
  if (!Array.isArray(value)) return [];

  return value.map((entry, index) => {
    const item = entry && typeof entry === 'object' ? /** @type {Record<string, unknown>} */ (entry) : {};
    const sizesRaw = pick(item, 'Sizes', 'sizes');
    const sizes = Array.isArray(sizesRaw)
      ? sizesRaw
          .map((sizeEntry) => {
            if (!sizeEntry || typeof sizeEntry !== 'object') return null;

            const size = /** @type {Record<string, unknown>} */ (sizeEntry);

            const eventStockRemainingRaw = pick(size, 'EventStockRemaining', 'eventStockRemaining');
            const eventStockAllocatedRaw = pick(size, 'EventStockAllocated', 'eventStockAllocated');
            return {
              sizeId: Number(pick(size, 'SizeId', 'sizeId') || 0),
              name: String(pick(size, 'Name', 'name') || ''),
              stockAvailable: Number(pick(size, 'StockAvailable', 'stockAvailable') || 0),
              // Allowlist flag: the API now sends every size and marks non-curated ones
              // disabled rather than removing them. Absent field defaults to enabled.
              isEnabledForPreference: pick(size, 'IsEnabledForPreference', 'isEnabledForPreference') !== false,
              isScheduledStock: Boolean(pick(size, 'IsScheduledStock', 'isScheduledStock')),
              hasEventStock: Boolean(pick(size, 'HasEventStock', 'hasEventStock')),
              eventStockAllocated:
                eventStockAllocatedRaw == null || eventStockAllocatedRaw === ''
                  ? undefined
                  : Number(eventStockAllocatedRaw),
              eventStockRemaining:
                eventStockRemainingRaw == null || eventStockRemainingRaw === ''
                  ? undefined
                  : Number(eventStockRemainingRaw),
            };
          })
          .filter(Boolean)
      : [];

    const apiLineId = Number(pick(item, 'LineId', 'lineId') || 0);
    const productId = Number(pick(item, 'ProductId', 'productId') || 0);
    const scheduleItemId = Number(pick(item, 'ScheduleItemId', 'scheduleItemId') || 0);

    return {
      // Always use a unique client id — API LineId can repeat across products.
      lineId: index + 1,
      apiLineId,
      dropId: Number(pick(item, 'DropId', 'dropId') || 0) || undefined,
      productId,
      scheduleItemId,
      isPendingProduct: Boolean(pick(item, 'IsPendingProduct', 'isPendingProduct')),
      name: String(pick(item, 'Name', 'name') || ''),
      sku: String(pick(item, 'Sku', 'sku') || ''),
      thumbUrl: String(pick(item, 'ThumbUrl', 'thumbUrl') || ''),
      dropName: String(pick(item, 'DropName', 'dropName') || ''),
      goLiveDate: String(pick(item, 'GoLiveDate', 'goLiveDate') || ''),
      expectedShipFrom: String(pick(item, 'ExpectedShipFrom', 'expectedShipFrom') || ''),
      maxQtyPerLine: (() => {
        const raw = pick(item, 'MaxQtyPerLine', 'maxQtyPerLine');
        if (raw == null || raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })(),
      // Campaign product pool: max / already requested by others / remaining left to claim.
      productMaxQty: (() => {
        const raw = pick(item, 'ProductMaxQty', 'productMaxQty');
        if (raw == null || raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })(),
      productRequestedQty: (() => {
        const raw = pick(item, 'ProductRequestedQty', 'productRequestedQty');
        if (raw == null || raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })(),
      productRemainingQty: (() => {
        const raw = pick(item, 'ProductRemainingQty', 'productRemainingQty');
        if (raw == null || raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })(),
      totalStock: Number(pick(item, 'TotalStock', 'totalStock') || 0),
      stockBadge: String(pick(item, 'StockBadge', 'stockBadge') || ''),
      isAvailable: pick(item, 'IsAvailable', 'isAvailable') !== false,
      isEventStockControlled: Boolean(pick(item, 'IsEventStockControlled', 'isEventStockControlled')),
      // Sets: 0 = standalone; products sharing a setId > 0 are one set (one outfit).
      setId: Number(pick(item, 'SetId', 'setId') || 0),
      setName: String(pick(item, 'SetName', 'setName') || ''),
      sortOrder: normalizeSortOrder(pick(item, 'SortOrder', 'sortOrder')),
      dropSortOrder: normalizeSortOrder(pick(item, 'DropSortOrder', 'dropSortOrder')),
      listIndex: index,
      sizes: /** @type {GiftingSize[]} */ (sizes),
    };
  });
}

/** @param {unknown} value */
function normalizeDrops(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;

      const item = /** @type {Record<string, unknown>} */ (entry);
      const dropId = Number(pick(item, 'DropId', 'dropId') || 0);

      if (!dropId) return null;

      return {
        dropId,
        name: String(pick(item, 'Name', 'name') || ''),
        expectedShipFrom: String(pick(item, 'ExpectedShipFrom', 'expectedShipFrom') || ''),
        goLiveDate: String(pick(item, 'GoLiveDate', 'goLiveDate') || ''),
        sortOrder: normalizeSortOrder(pick(item, 'SortOrder', 'sortOrder')),
      };
    })
    .filter(Boolean);
}

/** @param {unknown} value */
function normalizeAssets(value) {
  if (!Array.isArray(value)) return [];

  return value.map((entry) => {
    const item = entry && typeof entry === 'object' ? /** @type {Record<string, unknown>} */ (entry) : {};

    return {
      assetId: Number(pick(item, 'AssetId', 'assetId') || 0),
      assetType: String(pick(item, 'AssetType', 'assetType') || ''),
      title: String(pick(item, 'Title', 'title') || ''),
      url: String(pick(item, 'Url', 'url') || ''),
      urlUs: String(pick(item, 'UrlUs', 'urlUs') || ''),
      isHero: Boolean(pick(item, 'IsHero', 'isHero')),
      sortOrder: Number(pick(item, 'SortOrder', 'sortOrder') || 0),
    };
  });
}

/** @param {unknown} value */
function normalizeExistingLines(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;

      const item = /** @type {Record<string, unknown>} */ (entry);
      const qty = Number(pick(item, 'Qty', 'qty') || 0);

      return {
        productId: Number(pick(item, 'ProductId', 'productId') || 0),
        scheduleItemId: Number(pick(item, 'ScheduleItemId', 'scheduleItemId') || 0),
        sizeId: Number(pick(item, 'SizeId', 'sizeId') || 0),
        qty: qty > 0 ? qty : 1,
        productName: String(pick(item, 'ProductName', 'productName') || ''),
        sizeName: String(pick(item, 'SizeName', 'sizeName') || ''),
        sku: String(pick(item, 'Sku', 'sku') || ''),
        // Admin can lock specific lines on "request changes": false = must stay fixed.
        // Absent field defaults to reselectable.
        canReselect: pick(item, 'CanReselect', 'canReselect') !== false,
      };
    })
    .filter(Boolean);
}

/** @param {unknown} raw */
function normalizeSession(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const record = /** @type {Record<string, unknown>} */ (raw);
  const campaignRaw = pick(record, 'Campaign', 'campaign');
  const influencerRaw = pick(record, 'Influencer', 'influencer');
  const campaign = campaignRaw && typeof campaignRaw === 'object' ? /** @type {Record<string, unknown>} */ (campaignRaw) : {};
  const influencer = influencerRaw && typeof influencerRaw === 'object' ? /** @type {Record<string, unknown>} */ (influencerRaw) : {};
  const deliverables = normalizeDeliverables(pick(campaign, 'Deliverables', 'deliverables'));

  return {
    influencer: {
      name: String(pick(influencer, 'Name', 'name') || ''),
      firstName: String(pick(influencer, 'FirstName', 'firstName') || ''),
      lastName: String(pick(influencer, 'LastName', 'lastName') || ''),
      email: String(pick(influencer, 'Email', 'email') || ''),
      phone: String(pick(influencer, 'Phone', 'phone') || ''),
      igHandle: String(pick(influencer, 'IgHandle', 'igHandle') || ''),
    },
    campaign: {
      id: Number(pick(campaign, 'Id', 'id') || 0),
      name: String(pick(campaign, 'Name', 'name') || ''),
      campaignType: String(pick(campaign, 'CampaignType', 'campaignType') || ''),
      requiresRsvp: normalizeBoolean(pick(campaign, 'RequiresRsvp', 'requiresRsvp'), false),
      requiresDeliverables: normalizeBoolean(pick(campaign, 'RequiresDeliverables', 'requiresDeliverables'), false),
      minItemAllowance: Number(pick(campaign, 'MinItemAllowance', 'minItemAllowance') || 0),
      maxItemAllowance: Number(pick(campaign, 'MaxItemAllowance', 'maxItemAllowance') || 0),
      // Admin opt-ins (require DB migrations 1198/1199; default off/empty until then).
      hideSoldOutProducts: normalizeBoolean(pick(campaign, 'HideSoldOutProducts', 'hideSoldOutProducts'), false),
      countByOutfit: normalizeBoolean(pick(campaign, 'CountByOutfit', 'countByOutfit'), false),
      exactSelectionCount: Number(pick(campaign, 'ExactSelectionCount', 'exactSelectionCount') || 0),
      contentDeadline: String(pick(campaign, 'ContentDeadline', 'contentDeadline') || ''),
      contentDeadlineNote: String(pick(campaign, 'ContentDeadlineNote', 'contentDeadlineNote') || ''),
      briefHtml: String(pick(campaign, 'BriefHtml', 'briefHtml') || ''),
      agreementHtml: String(pick(campaign, 'AgreementHtml', 'agreementHtml') || ''),
      termsHtml: String(pick(campaign, 'TermsHtml', 'termsHtml') || ''),
      contentWaiverHtml: String(pick(campaign, 'ContentWaiverHtml', 'contentWaiverHtml') || ''),
      eventDate: String(pick(campaign, 'EventDate', 'eventDate') || ''),
      eventTime: String(pick(campaign, 'EventTime', 'eventTime') || ''),
      eventLocation: String(pick(campaign, 'EventLocation', 'eventLocation') || ''),
      rsvpDeadline: String(pick(campaign, 'RsvpDeadline', 'rsvpDeadline') || ''),
      deliverables,
    },
    drops: normalizeDrops(pick(record, 'Drops', 'drops')),
    products: normalizeProducts(pick(record, 'Products', 'products')),
    assets: normalizeAssets(pick(record, 'Assets', 'assets')),
    status: resolveSessionStatus(record),
    canSubmit: normalizeBoolean(pick(record, 'CanSubmit', 'canSubmit'), true),
    requiresIdentity: Boolean(pick(record, 'RequiresIdentity', 'requiresIdentity')),
    sessionToken: String(pick(record, 'Token', 'token') || ''),
    changesRequestedMessage: String(
      pick(record, 'ChangesRequestedMessage', 'changesRequestedMessage', 'ChangesRequestedReason', 'changesRequestedReason') || ''
    ),
    existingLines: normalizeExistingLines(pick(record, 'ExistingLines', 'existingLines')),
  };
}

/** @param {GiftingProduct} product */
function getProductKey(product) {
  return String(product.lineId);
}

/** @param {{ sizeId?: number, qty?: number } | undefined} entry */
function isActiveSelection(entry) {
  if (!entry || Number(entry.qty) <= 0) return false;
  const sizeId = Number(entry.sizeId);
  return Number.isFinite(sizeId) && sizeId > 0;
}

/**
 * Optional nullable number helper for product pool fields.
 * @param {unknown} raw
 * @returns {number | null}
 */
function normalizeOptionalNonNegInt(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/**
 * Per-invite line qty cap (MaxQtyPerLine). null = not set.
 * @param {GiftingProduct | undefined} product
 * @returns {number | null}
 */
function getProductQtyLimit(product) {
  return normalizeOptionalNonNegInt(product?.maxQtyPerLine);
}

/**
 * Campaign pool remaining for this product (ProductRemainingQty from admin).
 * Only enforced when ProductMaxQty is set — otherwise ignore remaining (influencer catalogs
 * may send 0/default remaining without meaning the style is sold out).
 * @param {GiftingProduct | undefined} product
 * @returns {number | null}
 */
function getProductRemainingQty(product) {
  const max = normalizeOptionalNonNegInt(product?.productMaxQty);
  if (max == null) return null;
  return normalizeOptionalNonNegInt(product?.productRemainingQty);
}

/**
 * Stock-side qty cap for a size.
 * Event remaining is used ONLY when the product/size is actually event-stock controlled
 * (`isEventStockControlled` or `hasEventStock`). A bare `eventStockRemaining: 0` default in
 * the payload must NOT zero out influencer warehouse stock.
 * @param {GiftingSize | undefined} size
 * @param {GiftingProduct | undefined} [product]
 * @returns {number} finite cap, or Infinity when stock does not constrain qty
 */
function getSizeStockCap(size, product) {
  if (!size) return 0;

  const eventControlled =
    product?.isEventStockControlled === true || size.hasEventStock === true;

  if (eventControlled) {
    // Available only with allocated remaining; never fall back to warehouse stock.
    return Math.max(0, Number(Number.isFinite(size.eventStockRemaining) ? size.eventStockRemaining : 0));
  }

  if (size.isScheduledStock === true && Number(size.stockAvailable) <= 0) {
    return Infinity;
  }
  return Math.max(0, Number(size.stockAvailable || 0));
}

/**
 * Effective max qty for a product+size line = min of:
 * - size/event stock cap
 * - maxQtyPerLine (per-invite line cap), when set
 * - productRemainingQty (campaign pool left after others' requests), when set
 * @param {GiftingProduct | undefined} product
 * @param {GiftingSize | undefined} size
 * @returns {number}
 */
function getEffectiveMaxQty(product, size) {
  // Product pool exhausted (or admin marked unavailable) → nothing selectable.
  if (product?.isAvailable === false) return 0;
  const productRemaining = getProductRemainingQty(product);
  if (productRemaining != null && productRemaining <= 0) return 0;

  const lineLimit = getProductQtyLimit(product);
  const stockCap = getSizeStockCap(size, product);
  let max = stockCap;
  if (lineLimit != null) {
    max = Math.min(max === Infinity ? lineLimit : max, lineLimit);
  }
  if (productRemaining != null) {
    max = Math.min(max === Infinity ? productRemaining : max, productRemaining);
  }
  if (max === Infinity) return 1;
  return Math.max(0, Math.floor(max));
}

/**
 * A size is pickable when allowlisted AND getEffectiveMaxQty >= 1.
 * @param {GiftingSize | undefined} size
 * @param {GiftingProduct | undefined} [product]
 */
function isSizeSelectable(size, product) {
  if (!size || size.isEnabledForPreference === false) return false;
  if (product?.isAvailable === false) return false;
  return getEffectiveMaxQty(product, size) >= 1;
}

/**
 * A size is sold out when its effective stock cap is 0, or the product pool is exhausted.
 * @param {GiftingSize | undefined} size
 * @param {GiftingProduct | undefined} [product]
 */
function isSizeSoldOut(size, product) {
  if (!size) return true;
  if (product?.isAvailable === false) return true;
  const productRemaining = getProductRemainingQty(product);
  if (productRemaining != null && productRemaining <= 0) return true;
  const cap = getSizeStockCap(size, product);
  if (cap === Infinity) return false;
  return cap <= 0;
}

/**
 * Whether a size is obtainable for the picker (respects product remaining + size/event stock).
 * @param {GiftingSize | undefined} size
 * @param {GiftingProduct | undefined} [product]
 */
function sizeHasStock(size, product) {
  if (!size) return false;
  if (product?.isAvailable === false) return false;
  const productRemaining = getProductRemainingQty(product);
  if (productRemaining != null && productRemaining <= 0) return false;
  return getSizeStockCap(size, product) > 0;
}

/**
 * A product is available for the picker when marked available, product remaining > 0 (when
 * sent), and at least one obtainable size.
 * @param {GiftingProduct} product
 */
function isProductAvailableForPicker(product) {
  if (!product || product.isAvailable === false) return false;
  const productRemaining = getProductRemainingQty(product);
  if (productRemaining != null && productRemaining <= 0) return false;
  return (product.sizes || []).some((size) => sizeHasStock(size, product));
}

/**
 * When the campaign opts into hideSoldOutProducts, drop products that have no obtainable size at
 * all. Sizes are intentionally kept intact so unavailable ones still render greyed/disabled and
 * crossed out (recipients can see the full run of sizes, not a partial list). Otherwise everything
 * is returned unchanged. Note: the server already omits fully sold-out products when this flag is
 * on — this is a client-side safety net so behaviour is consistent either way.
 * @param {GiftingSession} session
 * @param {GiftingProduct[]} products
 * @returns {GiftingProduct[]}
 */
function applySoldOutVisibility(session, products) {
  if (!session?.campaign?.hideSoldOutProducts) return products;
  return products.filter(isProductAvailableForPicker);
}

/** @param {GiftingSession} session @param {number} lineId */
function findProductByLineId(session, lineId) {
  return (session.products || []).find((product) => product.lineId === lineId);
}

/**
 * Groups the session's products into sets (setId > 0), in first-appearance order.
 * @param {GiftingSession} session
 * @returns {{ setId: number, setName: string, products: GiftingProduct[] }[]}
 */
function getSetGroups(session) {
  /** @type {Map<number, { setId: number, setName: string, products: GiftingProduct[] }>} */
  const map = new Map();
  for (const product of session.products || []) {
    const setId = Number(product.setId || 0);
    if (setId <= 0) continue;
    if (!map.has(setId)) {
      map.set(setId, { setId, setName: String(product.setName || '').trim(), products: [] });
    }
    map.get(setId)?.products.push(product);
  }
  return [...map.values()];
}

/** @param {{ products: GiftingProduct[] }} set @returns {number} */
function getSetSelectedMemberCount(set) {
  return set.products.filter((product) => isActiveSelection(selections[getProductKey(product)])).length;
}

/** @param {{ products: GiftingProduct[] }} set @returns {boolean} */
function isSetFullySelected(set) {
  return set.products.length > 0 && getSetSelectedMemberCount(set) === set.products.length;
}

/** @param {{ products: GiftingProduct[] }} set @returns {boolean} */
function isSetPartiallySelected(set) {
  const count = getSetSelectedMemberCount(set);
  return count > 0 && count < set.products.length;
}

/** @param {GiftingSession} session @returns {boolean} */
function isCountByOutfit(session) {
  return !!session?.campaign?.countByOutfit;
}

/**
 * Selection count in the campaign's counting unit. Item mode = number of selected lines.
 * Outfit mode = sets with any piece selected (1 each) + selected standalone items (their qty).
 * @param {GiftingSession} session
 * @returns {number}
 */
function getSelectionCount(session) {
  if (!isCountByOutfit(session)) return getSelectedCount();

  let outfits = 0;
  for (const entry of Object.values(selections)) {
    if (!isActiveSelection(entry)) continue;
    const product = findProductByLineId(session, entry.lineId);
    if (Number(product?.setId || 0) <= 0) outfits += Number(entry.qty) || 1;
  }
  for (const set of getSetGroups(session)) {
    // A set is an optional pair: picking one OR both pieces counts as a single outfit.
    if (getSetSelectedMemberCount(set) > 0) outfits += 1;
  }
  return outfits;
}

/**
 * Outfit slots currently "occupied" for the purposes of the max limit. A set with any piece
 * selected occupies exactly one slot (adding its second piece stays within the same slot), while
 * standalone items each occupy a slot. Sets are optional pairs, so this mirrors getSelectionCount.
 * @param {GiftingSession} session
 * @returns {number}
 */
function getOccupiedOutfitSlots(session) {
  if (!isCountByOutfit(session)) return getSelectedCount();

  let slots = 0;
  for (const entry of Object.values(selections)) {
    if (!isActiveSelection(entry)) continue;
    const product = findProductByLineId(session, entry.lineId);
    if (Number(product?.setId || 0) <= 0) slots += Number(entry.qty) || 1;
  }
  for (const set of getSetGroups(session)) {
    if (getSetSelectedMemberCount(set) > 0) slots += 1;
  }
  return slots;
}

/**
 * Effective selection limits in the campaign's counting unit. When countByOutfit is on and
 * exactSelectionCount > 0, min and max both collapse to that exact target.
 * @param {GiftingSession} session
 * @returns {{ outfit: boolean, exact: number, min: number, max: number }}
 */
function getSelectionLimits(session) {
  const campaign = session.campaign || {};
  const outfit = isCountByOutfit(session);
  const exact = outfit ? Number(campaign.exactSelectionCount || 0) : 0;
  let min = Number(campaign.minItemAllowance || 0);
  let max = Number(campaign.maxItemAllowance || 0);
  if (exact > 0) {
    min = exact;
    max = exact;
  }
  return { outfit, exact, min, max };
}

/** @param {GiftingSession} session @returns {{ setId: number, setName: string, products: GiftingProduct[] }[]} */
function getPartiallySelectedSets(session) {
  return getSetGroups(session).filter(isSetPartiallySelected);
}

/** @param {{ products: GiftingProduct[] }} set @returns {'complete' | 'partial' | 'none'} */
function getSetStatus(set) {
  if (isSetFullySelected(set)) return 'complete';
  if (isSetPartiallySelected(set)) return 'partial';
  return 'none';
}

/** @param {'complete' | 'partial' | 'none'} status @returns {string} */
function getSetHintText(status) {
  if (status === 'complete') return '✓ Both pieces added · counts as 1 outfit';
  if (status === 'partial') return 'You can also add the matching piece (optional) — still 1 outfit';
  return 'Set · add one or both pieces (counts as 1 outfit)';
}

/** @returns {boolean} */
function isUsShopifyStore() {
  const host = String(window.location.hostname || '').toLowerCase();
  if (host.includes('.com.au')) return false;
  if (host === 'saboskirt.com' || host === 'www.saboskirt.com' || host.includes('us.saboskirt.com')) {
    return true;
  }

  const shop = String(window.Shopify?.shop || '').toLowerCase();
  return shop.includes('.com') && !shop.includes('.com.au');
}

/** @param {GiftingAsset} asset */
function getAssetUrl(asset) {
  if (isUsShopifyStore() && asset.urlUs) return asset.urlUs;
  return asset.url || asset.urlUs || '';
}

/**
 * Renders a gifting image with mobile-safe defaults: lazy loading, async decode,
 * and a graceful fallback so a broken/expired src can't retry-storm the page.
 * @param {{ url?: string, alt?: string, className?: string, width: number, height: number, eager?: boolean }} options
 */
function renderGiftingImage(options) {
  const { url, alt = '', className = '', width, height, eager = false } = options;
  const src = String(url || '').trim();
  if (!src) return '';

  const classAttr = className ? ` class="${className}"` : '';
  const loadingAttr = eager ? '' : ' loading="lazy"';
  const priorityAttr = eager ? ' fetchpriority="high"' : ' fetchpriority="low"';

  return `<img${classAttr} src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" width="${width}" height="${height}"${loadingAttr} decoding="async"${priorityAttr} onerror="this.closest('.gifting-ambassador__product-media, .gifting-ambassador__hero') ? this.remove() : (this.style.display='none')">`;
}

/** @param {string | undefined} badge */
function isOutOfStockBadge(badge) {
  return String(badge || '').toLowerCase() === 'outofstock';
}

/** @param {string | undefined} badge */
function isLowStockBadge(badge) {
  return String(badge || '').toLowerCase() === 'low';
}

/** @type {GiftingSession | null} */
let sessionData = null;

/** @type {string} */
let inviteToken = '';

/** @type {string} */
let campaignCode = '';

/** @type {GiftingAccessMode} */
let accessMode = '';

/** @type {number} */
let currentStepIndex = 0;

/** @type {boolean} */
let isReadOnly = false;

/** @type {Record<string, { lineId: number, productId: number, scheduleItemId: number, isPendingProduct: boolean, sizeId: number, qty: number }>} */
let selections = {};

/** @type {{ firstName: string, lastName: string, email: string, phone: string, igHandle: string, addressLine1: string, city: string, postcode: string, country: string, notes: string, deadlineChoice: 'confirm' | 'need_time' | '', deliverableIds: number[], termsAccepted: boolean, dataConsentAccepted: boolean, attendance: 'Attending' | 'NotAttending' | '', dietaryRequirements: string, contentWaiverAccepted: boolean }} */
let formState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  igHandle: '',
  addressLine1: '',
  city: '',
  postcode: '',
  country: 'Australia',
  notes: '',
  deadlineChoice: '',
  deliverableIds: [],
  termsAccepted: false,
  dataConsentAccepted: false,
  attendance: '',
  dietaryRequirements: '',
  contentWaiverAccepted: false,
};

function getGiftingRoot() {
  const root = document.querySelector('[data-gifting-root]');
  return root instanceof HTMLElement ? root : null;
}

function isThemePreview() {
  return (
    window.location.hostname.includes('shopifypreview.com') ||
    Boolean(window.Shopify?.designMode) ||
    Boolean(window.Shopify?.visualPreviewMode)
  );
}

/**
 * Measures the theme's sticky header (which varies with announcement bars / sticky states) and
 * writes its height to `--gifting-sticky-top` on the gifting root so the sticky selection tray
 * pins directly below the nav instead of sliding under it. Theme CSS vars alone are unreliable
 * here because the header height changes on scroll.
 */
function updateGiftingStickyOffset() {
  const root = getGiftingRoot();
  if (!root) return;

  let bottom = 0;
  // `.header-section` is the element the theme makes `position: sticky` (top: -1px). `#header-group`
  // is display:contents (zero box) and `.header` itself is static, so those can't be measured.
  const candidates = ['.header-section', 'header.header', '.header', 'sticky-header'];
  for (const selector of candidates) {
    document.querySelectorAll(selector).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const style = window.getComputedStyle(el);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = el.getBoundingClientRect();
      // Only count a header currently pinned at (or just above) the top of the viewport.
      if (rect.top <= 2 && rect.height > 0 && rect.bottom > bottom) {
        bottom = rect.bottom;
      }
    });
  }

  // Only override the CSS fallback when we actually measured a pinned header; otherwise remove it
  // so the theme header vars take over (prevents collapsing the offset to ~0 and sliding under nav).
  if (bottom > 0) {
    root.style.setProperty('--gifting-sticky-top', `${Math.round(bottom)}px`);
  } else {
    root.style.removeProperty('--gifting-sticky-top');
  }
}

let giftingStickyOffsetBound = false;
/** Keeps --gifting-sticky-top in sync as the sticky header appears/collapses on scroll/resize. */
function bindGiftingStickyOffset() {
  updateGiftingStickyOffset();
  if (giftingStickyOffsetBound) return;
  giftingStickyOffsetBound = true;
  const onChange = () => window.requestAnimationFrame(updateGiftingStickyOffset);
  window.addEventListener('scroll', onChange, { passive: true });
  window.addEventListener('resize', onChange, { passive: true });
}

function getProxyBase() {
  const root = getGiftingRoot();
  const override = root ? String(root.dataset.apiBase || '').trim() : '';
  return override || DEFAULT_PROXY_BASE;
}

function resolvePreviewCampaignCode() {
  if (isReCollabLanding()) return '';

  if (!isThemePreview()) return '';

  const root = getGiftingRoot();
  return root ? String(root.dataset.previewCampaignCode || root.dataset.previewToken || '').trim() : '';
}

function isReCollabLanding() {
  const answer = (new URLSearchParams(window.location.search).get('answer') || '').trim().toLowerCase();
  return answer === 'yes' || answer === 'no';
}

function resolveInviteToken() {
  if (isReCollabLanding()) return '';

  const params = new URLSearchParams(window.location.search);
  return String(params.get('token') || '').trim();
}

function readStoredCampaignCode() {
  try {
    return String(sessionStorage.getItem(CAMPAIGN_CODE_STORAGE_KEY) || '').trim();
  } catch (_error) {
    return '';
  }
}

function persistCampaignCode(code) {
  campaignCode = String(code || '').trim();

  try {
    if (campaignCode) {
      sessionStorage.setItem(CAMPAIGN_CODE_STORAGE_KEY, campaignCode);
    } else {
      sessionStorage.removeItem(CAMPAIGN_CODE_STORAGE_KEY);
    }
  } catch (_error) {
    // Ignore storage failures.
  }
}

function clearStoredCampaignCode() {
  campaignCode = '';
  accessMode = '';

  try {
    sessionStorage.removeItem(CAMPAIGN_CODE_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage failures.
  }
}

/** @type {boolean} */
let igHandleLocked = false;

const GIFTING_PRODUCTS_PAGE_SIZE = 12;

/** @type {Record<string, number>} */
let visibleCountByDrop = {};

/** @type {Record<string, boolean>} */
let collapsedDrops = {};

/** @type {GiftingExistingLine[]} Previous picks that can no longer be sent (rechoose). */
let unfulfillableExistingLines = [];

/** @type {Set<string>} Product keys locked by the admin on rechoose (canReselect = false). */
let lockedProductKeys = new Set();

function resetProductListUiState() {
  visibleCountByDrop = {};
  collapsedDrops = {};
  unfulfillableExistingLines = [];
  lockedProductKeys = new Set();
}

/** @param {GiftingProduct} product @returns {boolean} */
function isProductLocked(product) {
  return !!product && lockedProductKeys.has(getProductKey(product));
}

function isCodeAccessMode() {
  return accessMode === 'code';
}

function isTokenAccessMode() {
  return accessMode === 'token';
}

/** @param {GiftingSession | null | undefined} session */
function isEventCampaign(session) {
  const campaign = session?.campaign;
  if (!campaign) return false;
  if (campaign.requiresRsvp === true) return true;
  return String(campaign.campaignType || '').trim().toLowerCase() === 'event';
}

/** @param {GiftingSession | null | undefined} session */
function isRechoose(session) {
  return normalizeSessionStatus(session?.status) === 'changesrequested';
}

/** RSVP is closed when an event campaign's rsvpDeadline is in the past. @param {GiftingSession | null | undefined} session */
function isRsvpClosed(session) {
  if (!isEventCampaign(session)) return false;
  const raw = String(session?.campaign?.rsvpDeadline || '').trim();
  if (!raw) return false;
  const deadline = Date.parse(raw);
  if (!Number.isFinite(deadline)) return false;
  return Date.now() > deadline;
}

/** @param {GiftingSession} session */
function getSubmitLabel(session) {
  const rechoose = isRechoose(session);
  if (isEventCampaign(session)) {
    if (formState.attendance === 'NotAttending') return 'Submit RSVP';
    return rechoose ? 'Resubmit RSVP' : 'Submit RSVP';
  }
  return rechoose ? 'Resubmit selection' : 'Submit preferences';
}

/** @param {GiftingSession | null | undefined} session */
function getActiveSteps(session) {
  if (session && isEventCampaign(session)) {
    if (formState.attendance === 'NotAttending') {
      return /** @type {const} */ (['rsvp', 'decline']);
    }
    // Rechoose skips the RSVP landing and goes straight to the attending form.
    if (isRechoose(session)) {
      return /** @type {const} */ (['products', 'details', 'terms']);
    }
    return /** @type {const} */ (['rsvp', 'products', 'details', 'terms']);
  }

  if (isCodeAccessMode() || isTokenAccessMode() || session?.requiresIdentity) {
    return /** @type {const} */ (['products', 'details', 'terms']);
  }
  return /** @type {const} */ (['products', 'terms']);
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** @param {string} html */
function sanitizeHtml(html) {
  const template = document.createElement('template');
  template.innerHTML = String(html ?? '');

  template.content.querySelectorAll('script, iframe, object, embed, link, meta, style').forEach((node) => {
    node.remove();
  });

  template.content.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on') || name === 'srcdoc') {
        node.removeAttribute(attr.name);
      }
    });
  });

  return template.innerHTML;
}

/** @param {'success' | 'error'} tone @param {string} title @param {string} message */
function showTerminal(tone, title, message) {
  hideViews(['terminal']);

  const circle = document.querySelector('[data-gifting-status-circle]');
  const titleEl = document.querySelector('[data-gifting-terminal-title]');
  const messageEl = document.querySelector('[data-gifting-terminal-message]');

  if (circle instanceof HTMLElement) {
    circle.className = `gifting-ambassador__status-circle gifting-ambassador__status-circle--${tone}`;
    circle.textContent = tone === 'success' ? '✓' : '✕';
  }

  if (titleEl instanceof HTMLElement) titleEl.textContent = title;
  if (messageEl instanceof HTMLElement) messageEl.textContent = message;
}

/** @param {Array<'loading' | 'unlock' | 'wizard' | 'terminal'>} visible */
function hideViews(visible) {
  const map = {
    loading: document.querySelector('[data-gifting-loading]'),
    unlock: document.querySelector('[data-gifting-unlock]'),
    wizard: document.querySelector('[data-gifting-wizard]'),
    terminal: document.querySelector('[data-gifting-terminal]'),
  };

  /** @type {Array<'loading' | 'unlock' | 'wizard' | 'terminal'>} */
  const keys = ['loading', 'unlock', 'wizard', 'terminal'];

  keys.forEach((key) => {
    const el = map[key];
    if (el instanceof HTMLElement) {
      el.hidden = !visible.includes(key);
    }
  });
}

function showLoading() {
  hideViews(['loading']);
}

function showUnlock() {
  hideViews(['unlock']);
  bindUnlockForm();
}

/** @param {string} message */
function setUnlockError(message) {
  const errorEl = document.querySelector('[data-gifting-unlock-error]');
  if (!(errorEl instanceof HTMLElement)) return;

  if (!message) {
    errorEl.hidden = true;
    errorEl.textContent = '';
    return;
  }

  errorEl.hidden = false;
  errorEl.textContent = message;
}

function bindUnlockForm() {
  const form = document.querySelector('[data-gifting-unlock-form]');
  if (!(form instanceof HTMLFormElement) || form.dataset.bound) return;

  form.dataset.bound = 'true';
  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const input = form.querySelector('[data-gifting-unlock-input]');
    const submitBtn = form.querySelector('[data-gifting-unlock-submit]');
    if (!(input instanceof HTMLInputElement) || !(submitBtn instanceof HTMLButtonElement)) return;

    const code = String(input.value || '').trim();
    if (!code) {
      setUnlockError('Please enter your campaign code.');
      return;
    }

    setUnlockError('');
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-busy', 'true');
    showLoading();

    await unlockByCampaignCode(code, { fromUnlock: true });

    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-busy');
  });
}

function rememberInviteTokenInUrl() {
  if (isThemePreview() || accessMode !== 'token' || !inviteToken) return;

  const url = new URL(window.location.href);
  url.searchParams.delete('code');

  if (url.searchParams.get('token') === inviteToken) return;

  url.searchParams.set('token', inviteToken);
  history.replaceState({}, '', url);
}

/** @param {string} message @param {string} [title] */
function showError(message, title) {
  showTerminal(
    'error',
    title || 'Unable to load invite',
    message || 'The campaign has closed or this invite is no longer valid. Reach out to the Sabo team if you think this is a mistake.'
  );
}

/** @param {GiftingSession} session @param {string} [message] */
function showSubmitted(session, message) {
  const firstName = formState.firstName || session.influencer?.firstName || splitName(session.influencer?.name).firstName || 'there';
  const title = isEventCampaign(session) ? `Your RSVP is in, ${firstName}` : `You're all submitted, ${firstName}`;
  showTerminal('success', title, getSubmittedMessage(session, message));
}

/** @param {string} name */
function splitName(name) {
  const parts = String(name || '').trim().split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || '',
  };
}

/** @param {string} token */
async function fetchSessionByToken(token) {
  const response = await fetch(`${getProxyBase()}/session?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  return parseProxyResponse(response, 'Unable to load invite. Please open the link from your email.');
}

/** @param {string} code */
async function fetchSessionByCampaignCode(code) {
  const response = await fetch(`${getProxyBase()}/unlock-code`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    cache: 'no-store',
    body: JSON.stringify({ CampaignCode: code }),
  });

  return parseProxyResponse(response, 'Unable to unlock this campaign code right now.');
}

/** @param {Response} response @param {string} authErrorMessage */
async function parseProxyResponse(response, authErrorMessage) {
  if (response.status === 401) {
    throw new Error(authErrorMessage);
  }

  if (response.status === 404) {
    throw new Error(
      isThemePreview()
        ? 'Gifting API not found on preview. Confirm the gifting app proxy is installed on this store, then reload.'
        : 'Unable to load gifting right now. Please try again shortly.'
    );
  }

  if (!response.ok) {
    throw new Error('Unable to load gifting right now. Please try again shortly.');
  }

  return response.json();
}

/** @param {unknown} data @param {{ fromUnlock?: boolean, invalidMessage?: string }} [options] */
function handleSessionPayload(data, options = {}) {
  const valid = isSessionPayloadValid(data);
  const message = String(pick(data, 'Message', 'message') || '');
  const session = normalizeSession(pick(data, 'Session', 'session'));

  if (!valid || !session) {
    if (options.fromUnlock) {
      showUnlock();
      setUnlockError(message || options.invalidMessage || 'That campaign code is not valid. Check your code and try again.');
    } else {
      const presentation = getInviteErrorPresentation(message);
      showError(presentation.message, presentation.title);
    }
    return null;
  }

  if (session.sessionToken && accessMode === 'token') {
    inviteToken = session.sessionToken;
  }

  if (!shouldShowGiftingWizard(session) && normalizeSessionStatus(session.status) === 'submitted') {
    showSubmitted(session);
    return null;
  }

  if (accessMode === 'token') {
    rememberInviteTokenInUrl();
  }

  renderSession(session);
  return session;
}

/** @returns {GiftingDeliverable[]} */
function getDeliverables(session) {
  return session.campaign?.deliverables || [];
}

/** @param {GiftingSession} session */
function initFormState(session) {
  const influencer = session.influencer || {};
  const split = splitName(influencer.name);

  formState = {
    firstName: influencer.firstName || split.firstName,
    lastName: influencer.lastName || split.lastName,
    email: influencer.email || '',
    phone: influencer.phone || '',
    igHandle: influencer.igHandle || '',
    addressLine1: '',
    city: '',
    postcode: '',
    country: 'Australia',
    notes: '',
    deadlineChoice: '',
    deliverableIds: [],
    termsAccepted: false,
    dataConsentAccepted: false,
    // Rechoose on an event goes straight to the attending form, so default attendance.
    attendance: isEventCampaign(session) && isRechoose(session) ? 'Attending' : '',
    dietaryRequirements: '',
    contentWaiverAccepted: false,
  };

  igHandleLocked = isTokenAccessMode() && Boolean(String(influencer.igHandle || '').trim());

  selections = {};
  resetProductListUiState();
  prefillSelectionsFromExistingLines(session);
}

/**
 * Pre-select the influencer's previous picks (rechoose) by matching existingLines to the
 * current catalog. Pending items match by scheduleItemId; live products by productId.
 * @param {GiftingSession} session
 */
function prefillSelectionsFromExistingLines(session) {
  const lines = session.existingLines || [];
  if (!lines.length || !Array.isArray(session.products)) return;

  for (const line of lines) {
    const sizeId = Number(line.sizeId);
    if (!(sizeId > 0)) continue;

    let product = null;
    if (Number(line.scheduleItemId) > 0) {
      product = session.products.find(
        (item) => Number(item.scheduleItemId) > 0 && Number(item.scheduleItemId) === Number(line.scheduleItemId)
      );
    }
    if (!product && Number(line.productId) > 0) {
      product = session.products.find(
        (item) => Number(item.productId) > 0 && Number(item.productId) === Number(line.productId)
      );
    }
    // Product no longer in the catalog — can't be sent, needs a replacement.
    if (!product) {
      unfulfillableExistingLines.push(line);
      continue;
    }

    // Previous size is now sold out / off the allowlist: don't pre-select it (would look
    // "Added" but can't ship). Flag it so we can prompt for a replacement.
    const sizeMeta = (product.sizes || []).find((s) => Number(s.sizeId) === sizeId);
    if (!isSizeSelectable(sizeMeta, product)) {
      unfulfillableExistingLines.push(line);
      continue;
    }

    const productKey = getProductKey(product);
    const requestedQty = Number(line.qty) > 0 ? Number(line.qty) : 1;
    const maxQty = getEffectiveMaxQty(product, sizeMeta);
    selections[productKey] = {
      lineId: product.lineId,
      productId: product.productId,
      scheduleItemId: product.scheduleItemId,
      isPendingProduct: product.isPendingProduct,
      sizeId,
      qty: Math.min(requestedQty, maxQty),
    };

    // Admin-locked line (canReselect = false): keep it fixed — non-editable in the picker.
    if (line.canReselect === false) {
      lockedProductKeys.add(productKey);
    }
  }
}

/** @param {GiftingSession} session */
function applyReadOnlyStatus(session) {
  const status = normalizeSessionStatus(session.status);

  if (status === 'submitted') {
    isReadOnly = true;
    showSubmitted(session);
    return true;
  }

  if (status === 'declined') {
    isReadOnly = true;
    showError('This gifting invite is no longer available.');
    return true;
  }

  // Personal token links and campaign codes should unlock the picker unless truly finished.
  if (isTokenAccessMode() || isCodeAccessMode()) {
    return false;
  }

  if (shouldShowGiftingWizard(session)) {
    return false;
  }

  if (status === 'approved') {
    isReadOnly = true;
    showTerminal(
      'success',
      'Approved',
      "Approved — we'll be in touch with next steps."
    );
    return true;
  }

  if (status === 'pending') {
    isReadOnly = true;
    showError(
      'This invite has not been sent yet. Ask the Sabo team to send your invite from the campaign admin.',
      'Invite not sent yet'
    );
    return true;
  }

  isReadOnly = true;
  showError('This gifting invite is no longer available.');
  return true;
}

/** @returns {number} */
function getSelectedCount() {
  return Object.values(selections).filter((entry) => isActiveSelection(entry)).length;
}

/** @param {GiftingSession} session */
function renderSelectionCountHtml(session) {
  // Event campaigns use the consolidated sticky selection tray instead of this strip.
  if (isEventCampaign(session)) return '';

  const { outfit, exact, min, max } = getSelectionLimits(session);
  const selectedCount = getSelectionCount(session);

  // Show the strip whenever there's a limit OR a half-picked set to prompt about — so the
  // "finish the set" message still appears on campaigns with no min/max.
  const partialSets = getPartiallySelectedSets(session);
  const hasLimits = min > 0 || max > 0 || exact > 0;
  if (!hasLimits && !partialSets.length) return '';

  const unit = outfit ? 'outfit' : 'item';
  const unitPlural = outfit ? 'outfits' : 'items';
  const pluralize = (n) => (n === 1 ? unit : unitPlural);
  const hasMin = min > 0;
  const hasMax = max > 0;
  const isExact = exact > 0 || (hasMin && hasMax && min === max);
  const target = isExact ? (exact > 0 ? exact : min) : hasMax ? max : min;
  const belowMin = hasMin && selectedCount < min;
  const atMax = hasMax && selectedCount >= max;
  const atMin = !belowMin;
  const progressTarget = target > 0 ? target : hasMin ? min : max;
  const progressPct =
    progressTarget > 0 ? Math.min(100, Math.round((selectedCount / progressTarget) * 100)) : 0;

  /** @type {'warning' | 'complete' | 'max'} */
  let state = 'warning';
  if (atMax) state = 'max';
  else if (atMin) state = 'complete';

  /** @type {string} */
  let countDisplay = target > 0 ? `${selectedCount}` : `${selectedCount}`;
  const countLabel = outfit
    ? `outfit${selectedCount === 1 ? '' : 's'} chosen`
    : `piece${selectedCount === 1 ? '' : 's'} selected`;
  /** @type {string} */
  let meta = '';
  /** @type {string} */
  let note = '';

  if (isExact) {
    countDisplay = `${selectedCount}<span class="gifting-ambassador__selection-progress-sep">/</span>${target}`;
    if (belowMin) {
      note = `Choose ${target - selectedCount} more (${target} ${pluralize(target)} required)`;
    } else {
      // Exact target reached: make it clear another pick means swapping the current one.
      note = `${target} ${pluralize(target)} chosen — deselect to swap, or continue`;
    }
  } else if (hasMax) {
    countDisplay = `${selectedCount}<span class="gifting-ambassador__selection-progress-sep">/</span>${max}`;
    if (belowMin && hasMin) {
      note = `Minimum ${min} · ${min - selectedCount} more needed`;
    } else if (atMax) {
      note = `Maximum of ${max} ${pluralize(max)} chosen · deselect one to swap`;
    } else if (hasMin && atMin) {
      const remaining = max - selectedCount;
      note = remaining > 0 ? `Minimum reached · up to ${remaining} more allowed` : 'Ready to continue';
    } else if (selectedCount === 0) {
      note = `Choose up to ${max} ${pluralize(max)}`;
    } else {
      note = `${max - selectedCount} more allowed`;
    }
  } else if (hasMin) {
    countDisplay = `${selectedCount}`;
    meta = `Min. ${min}`;
    if (belowMin) {
      note = `Choose at least ${min - selectedCount} more to continue`;
    } else {
      note = 'Minimum reached — add more if you like; we\'ll confirm your final picks';
    }
  }

  // A half-picked set is valid (one piece is enough); just nudge that the matching piece is optional.
  if (partialSets.length) {
    const name = partialSets[0].setName || 'set';
    const suggestion = `Tip: you can also add the matching piece from '${name}' — it still counts as one outfit.`;
    note = note ? `${note} · ${suggestion}` : suggestion;
    if (state === 'warning' && !belowMin) state = 'complete';
  }

  let ariaLabel = `${selectedCount} ${pluralize(selectedCount)} ${outfit ? 'chosen' : 'selected'}`;
  if (isExact) {
    ariaLabel = `${selectedCount} of ${target} required ${unitPlural} ${outfit ? 'chosen' : 'selected'}`;
  } else if (hasMax) {
    ariaLabel = `${selectedCount} of ${max} ${unitPlural} ${outfit ? 'chosen' : 'selected'}`;
    if (hasMin) ariaLabel += `, minimum ${min}`;
  } else if (hasMin) {
    ariaLabel = `${selectedCount} ${unitPlural} ${outfit ? 'chosen' : 'selected'}, minimum ${min}`;
  }
  if (note) ariaLabel += `. ${note}`;

  return `
    <div
      class="gifting-ambassador__selection-progress gifting-ambassador__selection-progress--${state}"
      data-gifting-selection-count
      role="status"
      aria-live="polite"
      aria-label="${escapeHtml(ariaLabel)}"
    >
      <div class="gifting-ambassador__selection-progress-head">
        <span class="gifting-ambassador__selection-progress-count" aria-hidden="true">${countDisplay}</span>
        <span class="gifting-ambassador__selection-progress-label">${countLabel}</span>
        ${meta ? `<span class="gifting-ambassador__selection-progress-meta">${escapeHtml(meta)}</span>` : ''}
      </div>
      <div class="gifting-ambassador__selection-progress-track" aria-hidden="true">
        <span class="gifting-ambassador__selection-progress-fill" style="width: ${progressPct}%"></span>
      </div>
      ${note ? `<p class="gifting-ambassador__selection-progress-note">${escapeHtml(note)}</p>` : ''}
    </div>
  `;
}

/** @param {GiftingProduct} product @param {GiftingSession} session */
function getProductShipFromDisplay(product, session) {
  const direct = String(product.expectedShipFrom || '').trim();
  if (direct) return direct;

  const dropId = Number(product.dropId || 0);
  if (!dropId || !session.drops?.length) return '';

  const drop = session.drops.find((entry) => entry.dropId === dropId);
  return String(drop?.expectedShipFrom || '').trim();
}

/** @param {GiftingProduct[]} products @param {GiftingSession} session */
function groupProductsByDrop(products, session) {
  /** @type {Map<string, { key: string, dropName: string, shipFrom: string, sortOrder: number, listIndex: number, products: GiftingProduct[] }>} */
  const groups = new Map();

  for (const product of products) {
    const dropId = Number(product.dropId || 0);
    const drop =
      dropId && session.drops?.length
        ? session.drops.find((entry) => entry.dropId === dropId)
        : null;
    const dropName = String(product.dropName || drop?.name || '').trim() || 'Collection';
    const key = dropId ? `drop-${dropId}` : `drop-${dropName.toLowerCase().replace(/\s+/g, '-')}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        dropName,
        shipFrom: String(drop?.expectedShipFrom || product.expectedShipFrom || '').trim(),
        sortOrder: normalizeSortOrder(drop?.sortOrder ?? product.dropSortOrder),
        listIndex: normalizeSortOrder(product.listIndex),
        products: [],
      });
    }

    groups.get(key)?.products.push(product);
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      products: [...group.products].sort(compareBySortOrder),
    }))
    .sort((a, b) => {
      const sortDiff = normalizeSortOrder(a.sortOrder) - normalizeSortOrder(b.sortOrder);
      if (sortDiff !== 0) return sortDiff;
      return normalizeSortOrder(a.listIndex) - normalizeSortOrder(b.listIndex);
    });
}

/** @param {Array<{ key: string }>} groups */
function ensureProductListUiState(groups) {
  groups.forEach((group) => {
    if (visibleCountByDrop[group.key] === undefined) {
      visibleCountByDrop[group.key] = GIFTING_PRODUCTS_PAGE_SIZE;
    }
    if (collapsedDrops[group.key] === undefined) {
      collapsedDrops[group.key] = false;
    }
  });
}

/** @param {GiftingSession} session */
function renderSelectedSummaryHtml(session) {
  // Event campaigns use the consolidated sticky selection tray instead of this summary.
  if (isEventCampaign(session)) return '';

  const items = getSelectedLineSummaries(session);
  if (!items.length) return '';

  const status = getSelectionStatusLine(session);

  const rows = items
    .map((item) => {
      const thumb = item.thumbUrl
        ? renderGiftingImage({
            url: item.thumbUrl,
            className: 'gifting-ambassador__selected-summary-thumb',
            width: 40,
            height: 48,
          })
        : '<span class="gifting-ambassador__selected-summary-thumb gifting-ambassador__selected-summary-thumb--empty" aria-hidden="true"></span>';

      // A locked (canReselect=false) line is fixed, so no Remove control for it.
      const removeBtn = item.locked
        ? '<span class="gifting-ambassador__selected-summary-locked" aria-hidden="true">🔒</span>'
        : `<button
            type="button"
            class="gifting-ambassador__selected-summary-remove"
            data-gifting-remove-selection
            data-line-id="${escapeHtml(String(item.lineId))}"
            aria-label="Remove ${escapeHtml(item.name)}${item.sizeName ? ` size ${escapeHtml(item.sizeName)}` : ''}"
          >Remove</button>`;

      return `
        <li class="gifting-ambassador__selected-summary-item">
          ${thumb}
          <div class="gifting-ambassador__selected-summary-copy">
            <span class="gifting-ambassador__selected-summary-name">${escapeHtml(item.name)}</span>
            ${item.sizeName ? `<span class="gifting-ambassador__selected-summary-size">Size ${escapeHtml(item.sizeName)}</span>` : ''}
          </div>
          ${removeBtn}
        </li>
      `;
    })
    .join('');

  const statusHtml = status.text
    ? `<p class="gifting-ambassador__selected-summary-status gifting-ambassador__selected-summary-status--${status.state}">${escapeHtml(status.text)}</p>`
    : '';

  return `
    <div class="gifting-ambassador__selected-summary gifting-ambassador__selected-summary--${status.state}" data-gifting-selected-summary role="status" aria-live="polite">
      <div class="gifting-ambassador__selected-summary-head">
        <p class="gifting-ambassador__selected-summary-label">Your selections (${items.length})</p>
        ${statusHtml}
      </div>
      <ul class="gifting-ambassador__selected-summary-list">${rows}</ul>
    </div>
  `;
}

/**
 * Premium, always-visible sticky selection tray for event campaigns. Consolidates the progress
 * count, the how-to prompt, and the chosen picks (each removable) into one bar that stays pinned
 * across the whole product grid — the single source of truth for "what have I chosen / what next".
 * Rendered as a direct child of the products step so `position: sticky` spans the grid.
 * @param {GiftingSession} session
 * @returns {string}
 */
function renderEventSelectionTray(session) {
  if (!isEventCampaign(session)) return '';

  const rechoose = isRechoose(session);
  const { outfit, exact, min, max } = getSelectionLimits(session);
  const count = getSelectionCount(session);
  const items = getSelectedLineSummaries(session);
  const status = getSelectionStatusLine(session);
  const unit = outfit ? 'outfit' : 'item';
  const unitP = outfit ? 'outfits' : 'items';

  const isExact = exact > 0 || (min > 0 && max > 0 && min === max);
  const target = isExact ? (exact > 0 ? exact : min) : max > 0 ? max : 0;

  let countLabel;
  if (target > 0) {
    countLabel = `${count} <span class="gifting-ambassador__tray-of">of</span> ${target}`;
  } else if (min > 0) {
    countLabel = `${count} <span class="gifting-ambassador__tray-of">/ min</span> ${min}`;
  } else {
    countLabel = `${count}`;
  }
  const countUnit = target > 0 ? (target === 1 ? unit : unitP) : count === 1 ? unit : unitP;

  const progressTarget = target > 0 ? target : min > 0 ? min : 0;
  const pct =
    progressTarget > 0
      ? Math.min(100, Math.round((count / progressTarget) * 100))
      : count > 0
        ? 100
        : 0;
  const showTrack = progressTarget > 0;

  const picks = items
    .map((item) => {
      const thumb = item.thumbUrl
        ? renderGiftingImage({
            url: item.thumbUrl,
            className: 'gifting-ambassador__tray-pick-thumb',
            width: 40,
            height: 50,
          })
        : '<span class="gifting-ambassador__tray-pick-thumb gifting-ambassador__tray-pick-thumb--empty" aria-hidden="true"></span>';
      const sizeTag = item.sizeName
        ? `<span class="gifting-ambassador__tray-pick-size">${escapeHtml(item.sizeName)}</span>`
        : '';
      if (item.locked) {
        return `<span class="gifting-ambassador__tray-pick is-locked" title="${escapeHtml(item.name)}">${thumb}${sizeTag}<span class="gifting-ambassador__tray-pick-lock" aria-hidden="true">🔒</span></span>`;
      }
      return `<button
          type="button"
          class="gifting-ambassador__tray-pick"
          data-gifting-remove-selection
          data-line-id="${escapeHtml(String(item.lineId))}"
          title="${escapeHtml(item.name)}"
          aria-label="Remove ${escapeHtml(item.name)}${item.sizeName ? ` size ${escapeHtml(item.sizeName)}` : ''}"
        >${thumb}${sizeTag}<span class="gifting-ambassador__tray-pick-x" aria-hidden="true">&times;</span></button>`;
    })
    .join('');

  const picksHtml = items.length
    ? `<div class="gifting-ambassador__tray-picks">${picks}</div>`
    : `<p class="gifting-ambassador__tray-empty">Tap a size on any piece below to add it to your ${escapeHtml(unit)}.</p>`;

  const noteText = status.text || `Tap a size on any piece below to choose your ${unit}.`;
  const heading = rechoose ? 'Update your outfit' : 'Select your outfit';

  // Show a Continue action right in the (always-visible) tray once the selection is valid, so the
  // user doesn't have to scroll to the bottom. Mirrors the requirement in validateProductSelection.
  // Sets are optional pairs, so a half-picked set does not block continuing.
  const canContinue =
    count > 0 && (min <= 0 || count >= min) && (max <= 0 || count <= max);
  const continueHtml = canContinue
    ? `<button type="button" class="gifting-ambassador__tray-continue" data-gifting-tray-continue>${rechoose ? 'Continue →' : 'Continue to details →'}</button>`
    : '';

  return `
    <div class="gifting-ambassador__tray gifting-ambassador__tray--${status.state}" data-gifting-selection-tray role="group" aria-label="Your selection">
      <div class="gifting-ambassador__tray-headline">
        <span class="gifting-ambassador__tray-title">${escapeHtml(heading)} <span class="req">*</span></span>
        <span class="gifting-ambassador__tray-count">${countLabel} <span class="gifting-ambassador__tray-unit">${escapeHtml(countUnit)}</span></span>
      </div>
      <p class="gifting-ambassador__tray-note" data-gifting-tray-note>${escapeHtml(noteText)}</p>
      ${showTrack ? `<div class="gifting-ambassador__tray-track" aria-hidden="true"><span class="gifting-ambassador__tray-fill" style="width:${pct}%"></span></div>` : ''}
      <div class="gifting-ambassador__tray-body">
        ${picksHtml}
        ${continueHtml}
      </div>
    </div>
  `;
}

/**
 * A single, always-visible status line describing how close the selection is to the requirement
 * (used in the sticky summary). Mirrors the progress strip's logic but as short, action-first copy.
 * @param {GiftingSession} session
 * @returns {{ text: string, state: 'warning' | 'complete' | 'max' }}
 */
function getSelectionStatusLine(session) {
  const { outfit, exact, min, max } = getSelectionLimits(session);
  const count = getSelectionCount(session);
  const unit = outfit ? 'outfit' : 'item';
  const unitP = outfit ? 'outfits' : 'items';
  const u = (n) => (n === 1 ? unit : unitP);

  const isExact = exact > 0 || (min > 0 && max > 0 && min === max);
  const target = isExact ? (exact > 0 ? exact : min) : 0;

  if (isExact) {
    if (count >= target) {
      return { text: `All set — ${target} of ${target} ${u(target)} chosen. Tap Remove to swap.`, state: 'complete' };
    }
    return { text: `Choose ${target - count} more (${target} ${u(target)} required)`, state: 'warning' };
  }
  if (max > 0 && count >= max) {
    return { text: `Maximum reached (${count} of ${max}). Tap Remove to swap.`, state: 'max' };
  }
  if (min > 0 && count < min) {
    return { text: `Choose ${min - count} more (minimum ${min})`, state: 'warning' };
  }
  if (min > 0) {
    return { text: `Minimum ${min} reached — add more or continue.`, state: 'complete' };
  }
  if (count > 0) {
    return { text: `${count} ${u(count)} chosen`, state: 'complete' };
  }
  return { text: '', state: 'warning' };
}

/**
 * Positive, action-first toast copy after a size chip is tapped — confirms the add/remove and
 * tells the influencer the next step (finish a set, choose more, or that they're all set).
 * @param {GiftingSession} session
 * @param {GiftingProduct} product
 * @param {GiftingSize | undefined} sizeMeta
 * @param {boolean} isDeselect
 * @returns {string}
 */
function buildSelectionToast(session, product, sizeMeta, isDeselect) {
  const name = (product?.name || 'Item').trim();
  const sizeName = (sizeMeta?.name || '').trim();
  const label = `${name}${sizeName ? ` (${sizeName})` : ''}`;

  if (isDeselect) {
    return `${label} removed`;
  }

  // A set member with its partner still unpicked: let them know the matching piece is optional.
  const setId = Number(product?.setId || 0);
  if (setId > 0) {
    const set = getSetGroups(session).find((s) => s.setId === setId);
    if (set && isSetPartiallySelected(set)) {
      return `${label} added — you can also add the matching piece from '${set.setName || 'set'}' (optional, still 1 outfit).`;
    }
  }

  const status = getSelectionStatusLine(session);
  return status.text ? `${label} added — ${status.text}` : `${label} added`;
}

/** @returns {boolean} Whether the on-screen gifting diagnostic is enabled via ?giftingDebug=1. */
function isGiftingDebugEnabled() {
  try {
    return /[?&]giftingDebug=1\b/.test(window.location.search);
  } catch (_) {
    return false;
  }
}

/**
 * On-screen diagnostic panel (opt-in via ?giftingDebug=1) so set/limit config can be verified
 * without opening the console. Confirms whether the payload actually carries setId (the single
 * thing set enforcement depends on).
 * @param {GiftingSession} session
 * @returns {string}
 */
function renderGiftingDebugPanel(session) {
  if (!isGiftingDebugEnabled()) return '';
  const c = session.campaign || {};
  const limits = getSelectionLimits(session);
  const setGroups = getSetGroups(session);
  const setLines = setGroups
    .map((s) => `#${s.setId} "${escapeHtml(s.setName || '(no name)')}" — ${s.products.length} pieces`)
    .join('<br>');
  const setsOk = setGroups.length > 0;

  // Surface product-limit + event-stock fields so we can verify the API payload (e.g. Aimee Skirt).
  const stockLines = (session.products || [])
    .slice(0, 12)
    .map((p) => {
      const sizes = (p.sizes || [])
        .map((s) => {
          const rem = Number.isFinite(s.eventStockRemaining) ? s.eventStockRemaining : '—';
          const cap = getSizeStockCap(s, p);
          const eff = getEffectiveMaxQty(p, s);
          return `${escapeHtml(s.name)}: wh=${s.stockAvailable} evtRem=${rem} cap=${cap === Infinity ? '∞' : cap} eff=${eff}${
            isSizeSelectable(s, p) ? '' : ' [off]'
          }`;
        })
        .join('; ');
      return `${escapeHtml(p.name)} · maxQty/line=${p.maxQtyPerLine == null ? 'null' : p.maxQtyPerLine} · prodMax=${p.productMaxQty == null ? 'null' : p.productMaxQty} · req=${p.productRequestedQty == null ? 'null' : p.productRequestedQty} · rem=${p.productRemainingQty == null ? 'null' : p.productRemainingQty} · avail=${p.isAvailable !== false} · evtCtrl=${!!p.isEventStockControlled} · ${sizes}`;
    })
    .join('<br>');

  const rows = [
    ['countByOutfit', String(!!c.countByOutfit)],
    ['exactSelectionCount', String(Number(c.exactSelectionCount || 0))],
    ['min / max allowance', `${Number(c.minItemAllowance || 0)} / ${Number(c.maxItemAllowance || 0)}`],
    ['computed limits', `exact ${limits.exact}, min ${limits.min}, max ${limits.max}`],
    ['products with setId', String((session.products || []).filter((p) => Number(p.setId || 0) > 0).length)],
    ['sets detected', setsOk ? setLines : '<strong style="color:#b42318">NONE — setId is 0/absent in payload</strong>'],
    ['stock / limits (first 12)', stockLines || '(no products)'],
  ];

  return `
    <div style="margin:0 0 12px;padding:12px 14px;border:1px solid #b42318;border-radius:8px;background:#fff7f6;font:12px/1.5 monospace;color:#321d18;">
      <strong style="display:block;margin-bottom:6px;">gifting debug (?giftingDebug=1)</strong>
      ${rows.map(([k, v]) => `<div><span style="color:#957662;">${k}:</span> ${v}</div>`).join('')}
      <div style="margin-top:6px;color:#957662;">${setsOk ? 'Sets present → half-picked sets will be blocked on Continue.' : 'No sets → items are standalone; set enforcement cannot fire (deploy migrations / link products as a set in admin).'}</div>
    </div>
  `;
}

/** @param {{ key: string, dropName: string, shipFrom: string, products: GiftingProduct[] }} group @param {GiftingSession} session */
function renderDropSection(group, session) {
  const visibleCount = visibleCountByDrop[group.key] ?? GIFTING_PRODUCTS_PAGE_SIZE;
  const collapsed = collapsedDrops[group.key] ?? false;
  const visibleProducts = group.products.slice(0, visibleCount);
  const remaining = group.products.length - visibleProducts.length;
  const loadMoreCount = Math.min(GIFTING_PRODUCTS_PAGE_SIZE, remaining);

  return `
    <section class="gifting-ambassador__drop${collapsed ? ' is-collapsed' : ''}" data-drop-key="${escapeHtml(group.key)}">
      <button
        type="button"
        class="gifting-ambassador__drop-toggle"
        data-gifting-drop-toggle
        data-drop-key="${escapeHtml(group.key)}"
        aria-expanded="${collapsed ? 'false' : 'true'}"
      >
        <span class="gifting-ambassador__drop-title">${escapeHtml(group.dropName)}</span>
        ${group.shipFrom ? `<span class="gifting-ambassador__drop-meta">Ships from ${escapeHtml(group.shipFrom)}</span>` : ''}
        ${
          remaining > 0
            ? `<span class="gifting-ambassador__drop-meta gifting-ambassador__drop-meta--truncated">${visibleProducts.length} of ${group.products.length} shown</span>`
            : `<span class="gifting-ambassador__drop-count">${group.products.length} piece${group.products.length === 1 ? '' : 's'}</span>`
        }
      </button>
      <div class="gifting-ambassador__drop-panel">
        <div class="gifting-ambassador__drop-panel-inner">
          <div class="gifting-ambassador__product-grid" data-gifting-product-grid>
            ${renderProductCells(visibleProducts, session, renderProductCard)}
          </div>
          ${
            remaining > 0
              ? `
                <div class="gifting-ambassador__load-more-block">
                  <p class="gifting-ambassador__load-more-meta">
                    Showing ${visibleProducts.length} of ${group.products.length} pieces in this drop
                  </p>
                  <button type="button" class="gifting-ambassador__load-more" data-gifting-load-more data-drop-key="${escapeHtml(group.key)}">
                    ${
                      remaining <= loadMoreCount
                        ? `View all ${remaining} remaining piece${remaining === 1 ? '' : 's'}`
                        : `View ${loadMoreCount} more piece${loadMoreCount === 1 ? '' : 's'} (${remaining} left)`
                    }
                  </button>
                </div>
              `
              : ''
          }
        </div>
      </div>
    </section>
  `;
}

/** @param {GiftingSession} session */
function getSelectedLineSummaries(session) {
  return Object.values(selections)
    .filter((entry) => isActiveSelection(entry))
    .map((entry) => {
      const product = session.products?.find((item) => item.lineId === entry.lineId);
      const size = product?.sizes?.find((item) => Number(item.sizeId) === Number(entry.sizeId));

      return {
        lineId: entry.lineId,
        sizeId: entry.sizeId,
        locked: product ? isProductLocked(product) : false,
        name: product?.name || 'Selected item',
        sizeName: size?.name || '',
        shipFrom: product ? getProductShipFromDisplay(product, session) : '',
        thumbUrl: product?.thumbUrl || '',
      };
    });
}

/** @param {GiftingSession} session */
function renderSubmitSummaryHtml(session) {
  const items = getSelectedLineSummaries(session);
  if (!items.length) return '';

  const rows = items
    .map((item) => {
      const metaParts = [];
      if (item.sizeName) metaParts.push(`Size ${item.sizeName}`);
      if (item.shipFrom) metaParts.push(`Ships from ${item.shipFrom}`);
      const meta = metaParts.length
        ? `<span class="gifting-ambassador__submit-summary-size">${metaParts.map((part) => escapeHtml(part)).join(' · ')}</span>`
        : '';

      return `
        <li class="gifting-ambassador__submit-summary-item">
          ${
            item.thumbUrl
              ? renderGiftingImage({
                  url: item.thumbUrl,
                  className: 'gifting-ambassador__submit-summary-thumb',
                  width: 40,
                  height: 48,
                })
              : ''
          }
          <div class="gifting-ambassador__submit-summary-copy">
            <span class="gifting-ambassador__submit-summary-name">${escapeHtml(item.name)}</span>
            ${meta}
          </div>
        </li>
      `;
    })
    .join('');

  return `
    <div class="gifting-ambassador__submit-summary">
      <h3 class="gifting-ambassador__submit-summary-title">Your selections</h3>
      <ul class="gifting-ambassador__submit-summary-list">${rows}</ul>
    </div>
  `;
}

/** @param {GiftingSession} session @param {string} [apiMessage] */
function getSubmittedMessage(session, apiMessage) {
  if (apiMessage) return apiMessage;

  if (isEventCampaign(session)) {
    if (formState.attendance === 'NotAttending') {
      return "Thanks for letting us know — we're sorry you can't make it this time. Hope to see you at the next one.";
    }
    return "Thank you — your RSVP has been submitted. We'll be in touch with the final details soon.";
  }

  if (isCodeAccessMode()) {
    return "You're in! We've received your submission and it's pending approval. We'll review your selections and email you once confirmed.";
  }

  return "We're reviewing your picks and will email you once approved. Keep an eye on your inbox.";
}

/** @param {GiftingSession} session */
function renderProgress(session) {
  const progress = document.querySelector('[data-gifting-progress]');
  if (!(progress instanceof HTMLElement) || isReadOnly) return;

  progress.innerHTML = getActiveSteps(session).map((step, index) => {
    const classes = [
      'gifting-ambassador__progress-dot',
      index <= currentStepIndex ? 'is-active' : '',
      index < currentStepIndex ? 'is-complete' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return `<span class="${classes}" aria-label="Step ${index + 1}: ${step}"></span>`;
  }).join('');
}

/** @param {GiftingSession} session @param {{ lead?: string, greeting?: string }} [options] */
function renderIntro(session, options = {}) {
  const root = document.querySelector('[data-gifting-root]');
  const firstName = formState.firstName || splitName(session.influencer?.name).firstName || 'there';
  const campaignName = session.campaign?.name || 'your gifting campaign';
  const min = Number(session.campaign?.minItemAllowance || 0);
  const max = Number(session.campaign?.maxItemAllowance || 0);
  const event = isEventCampaign(session);

  let lead = event
    ? 'Select your outfit below, then confirm your details.'
    : 'Select your pieces below, then complete your details.';

  if (min > 0 && max > 0 && min !== max) {
    lead = `Pick ${min} to ${max} ${event ? 'looks' : 'items'} below, then ${event ? 'confirm' : 'complete'} your details.`;
  } else if (max > 0) {
    lead = `Pick up to ${max} ${event ? 'look' : 'item'}${max === 1 ? '' : 's'} below, then ${event ? 'confirm' : 'complete'} your details.`;
  } else if (min > 0) {
    lead = `Pick at least ${min} ${event ? 'look' : 'item'}${min === 1 ? '' : 's'} below, then ${event ? 'confirm' : 'complete'} your details.`;
  }

  if (options.lead) lead = options.lead;

  const greeting =
    options.greeting ||
    (event
      ? `Hi ${escapeHtml(firstName)}, you're invited to ${escapeHtml(campaignName)}`
      : `Hi ${escapeHtml(firstName)}, welcome to ${escapeHtml(campaignName)}`);

  return `
    <div class="gifting-ambassador__intro">
      <p class="gifting-ambassador__programme">${escapeHtml(root instanceof HTMLElement ? root.dataset.programmeLabel || 'Gifting programme' : 'Gifting programme')}</p>
      <h1 class="gifting-ambassador__greeting">${greeting}</h1>
      <p class="gifting-ambassador__lead">${lead}</p>
    </div>
  `;
}

/** @param {GiftingSession} session */
function renderEventMeta(session) {
  if (!isEventCampaign(session)) return '';

  const campaign = session.campaign || {};
  /** @type {Array<[string, string]>} */
  const rows = [];
  if (campaign.eventDate) rows.push(['Date', String(campaign.eventDate)]);
  if (campaign.eventTime) rows.push(['Time', String(campaign.eventTime)]);
  if (campaign.eventLocation) rows.push(['Location', String(campaign.eventLocation)]);
  if (campaign.rsvpDeadline) rows.push(['RSVP by', String(campaign.rsvpDeadline)]);

  if (!rows.length) return '';

  return `
    <div class="gifting-ambassador__event-meta">
      ${rows
        .map(
          ([label, value]) => `
            <div class="gifting-ambassador__event-meta-row">
              <span class="gifting-ambassador__event-meta-label">${escapeHtml(label)}</span>
              <span class="gifting-ambassador__event-meta-value">${escapeHtml(value)}</span>
            </div>
          `
        )
        .join('')}
    </div>
  `;
}

/** @param {GiftingSession} session */
function renderRechooseBanner(session) {
  if (!isRechoose(session)) return '';

  const message = String(session.changesRequestedMessage || '').trim();
  const title = isEventCampaign(session) ? 'Please update your RSVP' : 'Please update your selection';

  const names = unfulfillableExistingLines.map((line) => String(line.productName || '').trim()).filter(Boolean);
  const count = unfulfillableExistingLines.length;
  let unavailableNote = '';
  if (count) {
    unavailableNote = names.length
      ? `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} no longer available — please choose a replacement below.`
      : `${count} of your previous ${count === 1 ? 'pick is' : 'picks are'} no longer available — please choose a replacement below.`;
  }

  return `
    <div class="gifting-ambassador__rechoose-banner" role="status" aria-live="polite">
      <p class="gifting-ambassador__rechoose-banner-title">${escapeHtml(title)}</p>
      ${message ? `<p class="gifting-ambassador__rechoose-banner-note">${escapeHtml(message)}</p>` : ''}
      ${unavailableNote ? `<p class="gifting-ambassador__rechoose-banner-note gifting-ambassador__rechoose-banner-note--alert">${escapeHtml(unavailableNote)}</p>` : ''}
    </div>
  `;
}

/**
 * @param {GiftingProduct} product @param {GiftingSession} session
 * @param {{ setName: string, index: number, total: number }} [setContext]
 */
function renderProductCard(product, session, setContext) {
  const outOfStock = isOutOfStockBadge(product.stockBadge) || product.isAvailable === false;
  const availableSizes = (product.sizes || []).filter((size) => isSizeSelectable(size, product));
  const disabled = outOfStock || availableSizes.length === 0;
  const productKey = getProductKey(product);
  const selection = selections[productKey];
  const isSelected = isActiveSelection(selection);
  const lowStock = isLowStockBadge(product.stockBadge);
  const shipFrom = getProductShipFromDisplay(product, session);

  const locked = isProductLocked(product);
  const sizeChips = availableSizes
    .map((size) => {
      const selected = Number(selection?.sizeId) === Number(size.sizeId);
      return `
        <button
          type="button"
          class="gifting-ambassador__size-chip${selected ? ' is-selected' : ''}"
          data-line-id="${product.lineId}"
          data-size-id="${size.sizeId}"
          ${disabled || isReadOnly || locked ? 'disabled' : ''}
        >${escapeHtml(size.name)}</button>
      `;
    })
    .join('');

  return `
    <article class="gifting-ambassador__product-card${isSelected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}${locked ? ' is-locked' : ''}${setContext ? ' is-set-member' : ''}" data-line-id="${product.lineId}">
      <div class="gifting-ambassador__product-media">
        ${
          setContext
            ? `<span class="gifting-ambassador__set-tag">Set${setContext.setName ? ` · ${escapeHtml(setContext.setName)}` : ''}</span>`
            : ''
        }
        ${
          product.thumbUrl
            ? renderGiftingImage({
                url: product.thumbUrl,
                alt: product.name,
                width: 300,
                height: 400,
              })
            : ''
        }
      </div>
      <div class="gifting-ambassador__product-info">
        <p class="gifting-ambassador__product-name">${escapeHtml(product.name)}</p>
        ${product.dropName ? `<p class="gifting-ambassador__product-drop">${escapeHtml(product.dropName)}</p>` : ''}
        ${
          shipFrom
            ? `<p class="gifting-ambassador__product-ship"><span class="gifting-ambassador__product-ship-label">Ships from</span> ${escapeHtml(shipFrom)}</p>`
            : ''
        }
        ${product.isPendingProduct ? '<span class="gifting-ambassador__stock-badge">Coming soon</span>' : ''}
        ${disabled ? `<span class="gifting-ambassador__stock-badge">${outOfStock ? 'Out of stock' : 'Unavailable'}</span>` : `<div class="gifting-ambassador__size-row">${sizeChips}</div>`}
        ${lowStock && !disabled ? '<span class="gifting-ambassador__stock-badge">Low stock</span>' : ''}
        ${
          locked
            ? '<div class="gifting-ambassador__added-badge gifting-ambassador__added-badge--locked">🔒 Locked</div>'
            : isSelected
              ? '<div class="gifting-ambassador__added-badge">✓ Added</div>'
              : ''
        }
      </div>
    </article>
  `;
}

/**
 * Premium collection card for event campaigns. Mirrors the storefront quick-shop:
 * the full size range is shown as a minimal strip; unavailable sizes are muted and
 * disabled (no strikethrough) to keep the card clean and premium.
 * @param {GiftingProduct} product @param {GiftingSession} session
 * @param {{ setName: string, index: number, total: number }} [setContext]
 */
function renderEventProductCard(product, session, setContext) {
  const outOfStock = isOutOfStockBadge(product.stockBadge) || product.isAvailable === false;
  const sizes = product.sizes || [];
  const productKey = getProductKey(product);
  const selection = selections[productKey];
  const isSelected = isActiveSelection(selection);
  const lowStock = isLowStockBadge(product.stockBadge);
  const shipFrom = getProductShipFromDisplay(product, session);

  // Always render the full size range (matching the storefront collection card). Selectable
  // sizes are active; sold-out or not-allowlisted sizes are shown but disabled/greyed. We
  // never fade the whole card — availability is communicated per size instead.
  const locked = isProductLocked(product);
  const sizeButtons = sizes
    .map((size) => {
      const soldOut = isSizeSoldOut(size, product);
      const selectable = isSizeSelectable(size, product);
      const selected = Number(selection?.sizeId) === Number(size.sizeId);
      // Scheduled/pending sizes are selectable but not yet in the warehouse — don't strike
      // them through; only genuinely sold-out sizes get the strikethrough + "sold out".
      const unavailableLabel = soldOut ? ' — sold out' : ' — unavailable';
      return `
        <button
          type="button"
          class="gifting-ambassador__size-chip${selected ? ' is-selected' : ''}${selectable ? '' : ' is-unavailable'}"
          data-line-id="${product.lineId}"
          data-size-id="${size.sizeId}"
          ${selectable && !isReadOnly && !locked ? '' : 'disabled'}
          aria-label="${escapeHtml(size.name)}${selectable ? '' : unavailableLabel}"
        >
          <span class="gifting-ambassador__size-chip-label">${escapeHtml(size.name)}</span>
        </button>
      `;
    })
    .join('');

  return `
    <article class="gifting-ambassador__product-card${isSelected ? ' is-selected' : ''}${locked ? ' is-locked' : ''}${setContext ? ' is-set-member' : ''}" data-line-id="${product.lineId}">
      <div class="gifting-ambassador__product-media">
        ${
          setContext
            ? `<span class="gifting-ambassador__set-tag">Set${setContext.setName ? ` · ${escapeHtml(setContext.setName)}` : ''}</span>`
            : ''
        }
        ${
          product.thumbUrl
            ? renderGiftingImage({ url: product.thumbUrl, alt: product.name, width: 400, height: 533 })
            : ''
        }
      </div>
      ${sizes.length ? `<div class="gifting-ambassador__size-strip" role="group" aria-label="Select a size">${sizeButtons}</div>` : ''}
      <div class="gifting-ambassador__product-info">
        <p class="gifting-ambassador__product-name">${escapeHtml(product.name)}</p>
        ${product.dropName ? `<p class="gifting-ambassador__product-drop">${escapeHtml(product.dropName)}</p>` : ''}
        ${
          shipFrom
            ? `<p class="gifting-ambassador__product-ship"><span class="gifting-ambassador__product-ship-label">Ships from</span> ${escapeHtml(shipFrom)}</p>`
            : ''
        }
        ${
          outOfStock
            ? '<span class="gifting-ambassador__stock-badge">Sold out</span>'
            : lowStock
              ? '<span class="gifting-ambassador__stock-badge">Low stock</span>'
              : ''
        }
        ${
          locked
            ? '<div class="gifting-ambassador__added-badge gifting-ambassador__added-badge--locked">🔒 Locked</div>'
            : isSelected
              ? `<div class="gifting-ambassador__added-badge">✓ Added${(() => {
                  const n = (sizes.find((s) => Number(s.sizeId) === Number(selection?.sizeId)) || {}).name;
                  return n ? ` · Size ${escapeHtml(n)}` : '';
                })()}</div>`
              : ''
        }
        ${
          sizes.length
            ? `<button type="button" class="gifting-ambassador__size-trigger${isSelected ? ' is-selected' : ''}" data-gifting-size-trigger data-line-id="${product.lineId}"${isReadOnly || locked ? ' disabled' : ''}>${
                locked
                  ? '<span class="gifting-ambassador__size-trigger-label">Locked</span>'
                  : isSelected
                    ? `<span class="gifting-ambassador__size-trigger-label">Size ${escapeHtml((sizes.find((s) => Number(s.sizeId) === Number(selection?.sizeId)) || {}).name || '')}</span>${isReadOnly ? '' : '<span class="gifting-ambassador__size-trigger-change">Change</span>'}`
                    : '<span class="gifting-ambassador__size-trigger-label">Select size</span><span class="gifting-ambassador__size-trigger-icon" aria-hidden="true">+</span>'
              }</button>`
            : ''
        }
      </div>
    </article>
  `;
}

/**
 * Groups a set's members with a display:contents wrapper so each member stays a normal grid cell
 * (aligned/gapless with standalone items). Membership is shown per-card via a "Set" tag + accent;
 * sets are optional pairs (one piece is enough), with the matching piece suggested but not required.
 * @param {{ setId: number, setName: string, products: GiftingProduct[] }} set
 * @param {GiftingSession} session
 * @param {(product: GiftingProduct, session: GiftingSession, setContext?: { setName: string, index: number, total: number }) => string} memberRenderer
 */
function renderSetCard(set, session, memberRenderer) {
  const status = getSetStatus(set);
  const complete = status === 'complete';
  const partial = status === 'partial';
  const label = set.setName || 'Set';
  const members = set.products
    .map((product, index) =>
      memberRenderer(product, session, { setName: label, index, total: set.products.length })
    )
    .join('');

  return `<div class="gifting-ambassador__set${complete ? ' is-complete' : ''}${partial ? ' is-partial' : ''}" data-set-id="${set.setId}">${members}</div>`;
}

/**
 * Renders a product list, collapsing set members (setId > 0) into a single set card at the
 * first member's position and rendering standalone products individually.
 * @param {GiftingProduct[]} products
 * @param {GiftingSession} session
 * @param {(product: GiftingProduct, session: GiftingSession) => string} memberRenderer
 */
function renderProductCells(products, session, memberRenderer) {
  /** @type {Set<number>} */
  const seenSets = new Set();
  const cells = [];

  for (const product of products) {
    const setId = Number(product.setId || 0);
    if (setId > 0) {
      if (seenSets.has(setId)) continue;
      seenSets.add(setId);
      const set = {
        setId,
        setName: String(product.setName || '').trim(),
        products: products.filter((entry) => Number(entry.setId || 0) === setId),
      };
      cells.push(renderSetCard(set, session, memberRenderer));
    } else {
      cells.push(memberRenderer(product, session));
    }
  }

  return cells.join('');
}

/** @param {GiftingSession} session */
function getHeroImageUrl(session) {
  const heroAsset =
    [...(session.assets || [])]
      .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
      .find((asset) => asset.isHero && String(asset.assetType).toLowerCase() === 'image') ||
    session.assets?.find((asset) => String(asset.assetType).toLowerCase() === 'image');

  return { heroAsset, heroUrl: heroAsset ? getAssetUrl(heroAsset) : '' };
}

/** @param {GiftingAsset} asset */
function isVideoAsset(asset) {
  const type = String(asset?.assetType || '').toLowerCase();
  if (type === 'video' || type === 'mp4') return true;
  return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(getAssetUrl(asset) || '');
}

/** @param {GiftingAsset} asset */
function isImageAsset(asset) {
  const type = String(asset?.assetType || '').toLowerCase();
  if (type === 'image' || type === 'img' || type === 'photo') return true;
  return /\.(jpe?g|png|webp|avif|gif)(\?|#|$)/i.test(getAssetUrl(asset) || '');
}

/**
 * Resolves the best hero media (image or video) for a campaign. Prefers a hero-flagged
 * asset, then any video (cinematic), then any image.
 * @param {GiftingSession} session
 * @returns {{ type: 'video' | 'image' | '', url: string, asset: GiftingAsset | null }}
 */
function getHeroMedia(session) {
  const assets = [...(session.assets || [])].sort(
    (a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0)
  );

  const hero =
    assets.find((asset) => asset.isHero && (isVideoAsset(asset) || isImageAsset(asset))) ||
    assets.find((asset) => isVideoAsset(asset)) ||
    assets.find((asset) => isImageAsset(asset)) ||
    null;

  if (!hero) return { type: '', url: '', asset: null };

  return {
    type: isVideoAsset(hero) ? 'video' : 'image',
    url: getAssetUrl(hero),
    asset: hero,
  };
}

/**
 * Cinematic hero for event campaigns, constrained to the collection content width so it
 * lines up with the product rows. Supports an autoplaying muted video or an image, with a
 * gradient overlay and title copy. Falls back to an editorial text header when no asset.
 * @param {GiftingSession} session
 * @param {{ eyebrow?: string, title?: string, subtitle?: string }} [options]
 */
function renderEventHero(session, options = {}) {
  const root = getGiftingRoot();
  const { type, url, asset } = getHeroMedia(session);
  const eyebrow =
    options.eyebrow ||
    (root instanceof HTMLElement ? root.dataset.programmeLabel || 'Gifting programme' : 'Gifting programme');
  const title = options.title || session.campaign?.name || "You're invited";
  const subtitle = options.subtitle || '';

  let media = '';
  if (type === 'video' && url) {
    media = `<video class="gifting-ambassador__event-hero-media" autoplay muted loop playsinline preload="metadata" aria-hidden="true"><source src="${escapeHtml(url)}"></video>`;
  } else if (type === 'image' && url) {
    media = renderGiftingImage({
      url,
      alt: asset?.title || title,
      className: 'gifting-ambassador__event-hero-media',
      width: 1600,
      height: 900,
      eager: true,
    });
  }

  // Heading block, rendered either as a standalone editorial header (no banner) or beneath the
  // banner. Kept as text on the page (not overlaid) so it never obscures the banner artwork.
  const headerHtml = `
    <header class="gifting-ambassador__event-header">
      <p class="gifting-ambassador__event-header-eyebrow">${escapeHtml(eyebrow)}</p>
      <h1 class="gifting-ambassador__event-header-title">${escapeHtml(title)}</h1>
      ${subtitle ? `<p class="gifting-ambassador__event-header-subtitle">${escapeHtml(subtitle)}</p>` : ''}
    </header>
  `;

  // No banner asset — degrade to a clean editorial header aligned to the page gutters
  // (avoids an empty coloured band and keeps the page seamless).
  if (!media) {
    return headerHtml;
  }

  // The banner carries its own branding, so we never overlay text on top of it (that reads as if
  // the text is covering the artwork). Always show the banner clean; when hero text is enabled,
  // place the heading BELOW the banner so nothing obscures it.
  const cleanBanner = `
    <div class="gifting-ambassador__event-hero gifting-ambassador__event-hero--clean">
      ${media}
    </div>
  `;

  const hideHeroText =
    root instanceof HTMLElement && root.dataset.hideHeroText === 'true';
  if (hideHeroText) {
    return cleanBanner;
  }
  return `${cleanBanner}${headerHtml}`;
}

/** RSVP landing for event campaigns: "Will you be joining us?" @param {GiftingSession} session */
function renderRsvpStep(session) {
  const container = document.querySelector('[data-gifting-step="rsvp"]');
  if (!(container instanceof HTMLElement)) return;

  const firstName = formState.firstName || splitName(session.influencer?.name).firstName || 'there';
  const briefHtml = String(session.campaign?.briefHtml || '').trim();
  const rsvpClosed = isRsvpClosed(session);

  container.innerHTML = `
    ${renderEventHero(session, {
      title: "You're invited",
      subtitle: `Hi ${firstName}, you're invited to join us`,
    })}
    <div class="gifting-ambassador__event-inner">
      ${renderEventMeta(session)}
      ${briefHtml ? `<div class="gifting-ambassador__brief rte">${sanitizeHtml(briefHtml)}</div>` : ''}
      <p class="gifting-ambassador__section-label">Will you be joining us? <span class="req">*</span></p>
      ${
        rsvpClosed
          ? '<p class="gifting-ambassador__inline-error">RSVPs for this event have closed.</p>'
          : `
            <div class="gifting-ambassador__rsvp-actions">
              <button type="button" class="gifting-ambassador__btn-primary" data-gifting-rsvp="attending">Yes, I'll be there</button>
              <button type="button" class="gifting-ambassador__btn-secondary" data-gifting-rsvp="notattending">Sorry, I can't make it</button>
            </div>
          `
      }
      <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    </div>
  `;
}

/** Minimal "can't make it" form for event campaigns. @param {GiftingSession} session */
function renderDeclineStep(session) {
  const container = document.querySelector('[data-gifting-step="decline"]');
  if (!(container instanceof HTMLElement)) return;

  const rsvpClosed = isRsvpClosed(session);
  const submitDisabled = !session.canSubmit || rsvpClosed;

  container.innerHTML = `
    <h2 class="gifting-ambassador__form-section-title">Sorry you can't make it</h2>
    <p class="gifting-ambassador__lead">No problem — just confirm your details below and we'll note your RSVP.</p>
    <div class="gifting-ambassador__form-grid">
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-first-name">First name</label>
        <input class="gifting-ambassador__input" id="gifting-first-name" data-field="firstName" value="${escapeHtml(formState.firstName)}">
      </div>
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-last-name">Last name</label>
        <input class="gifting-ambassador__input" id="gifting-last-name" data-field="lastName" value="${escapeHtml(formState.lastName)}">
      </div>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-email">Email</label>
      <input class="gifting-ambassador__input" id="gifting-email" type="email" data-field="email" value="${escapeHtml(formState.email)}">
    </div>
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-submit ${submitDisabled ? 'disabled' : ''}>${escapeHtml(getSubmitLabel(session))}</button>
      <button type="button" class="gifting-ambassador__btn-secondary" data-gifting-back>← Back</button>
    </div>
  `;
}

/** @param {GiftingSession} session */
function renderProductsStep(session) {
  const container = document.querySelector('[data-gifting-step="products"]');
  if (!(container instanceof HTMLElement)) return;

  // Diagnostic (opt-in via ?giftingDebug=1): shows why the selection strip does/doesn't render.
  try {
    if (/[?&]giftingDebug=1\b/.test(window.location.search)) {
      const c = session.campaign || {};
      const setIds = (session.products || []).map((p) => Number(p.setId || 0)).filter((n) => n > 0);
      console.info('[gifting][debug] selection limits', {
        countByOutfit: !!c.countByOutfit,
        exactSelectionCount: Number(c.exactSelectionCount || 0),
        minItemAllowance: Number(c.minItemAllowance || 0),
        maxItemAllowance: Number(c.maxItemAllowance || 0),
        computedLimits: getSelectionLimits(session),
        productsWithSetId: setIds.length,
        distinctSets: [...new Set(setIds)],
        stripWillRender:
          getSelectionLimits(session).min > 0 ||
          getSelectionLimits(session).max > 0 ||
          getSelectionLimits(session).exact > 0,
      });
    }
  } catch (_) {
    /* no-op */
  }

  const event = isEventCampaign(session);
  // When the campaign hides sold-out items, filter them out here (single source feeding
  // both the event and influencer layouts); otherwise they stay and render disabled.
  const products = applySoldOutVisibility(session, [...(session.products || [])]);

  // Keep the sticky tray pinned below the theme header (measured live).
  bindGiftingStickyOffset();

  // Event campaigns get a premium collection-page layout: full-bleed hero + gapless grid.
  if (event) {
    renderEventProductsStep(session, container, products);
    return;
  }

  const dropGroups = groupProductsByDrop(products, session);
  ensureProductListUiState(dropGroups);

  const { heroAsset, heroUrl } = getHeroImageUrl(session);
  const briefHtml = String(session.campaign?.briefHtml || '').trim();
  const productCatalog = dropGroups.length
    ? `<div class="gifting-ambassador__drop-list" data-gifting-drop-list>${dropGroups.map((group) => renderDropSection(group, session)).join('')}</div>`
    : '';
  const sectionLabel = event ? 'Select your outfit' : 'Select your pieces';
  // The RSVP landing (event, non-rechoose) already showed the intro/hero/brief; avoid repeating
  // them there. Rechoose skips the landing, so still show the intro blocks in that case.
  const wentThroughRsvpLanding = event && !isRechoose(session);
  const showIntroBlocks = !wentThroughRsvpLanding;

  container.innerHTML = `
    ${renderGiftingDebugPanel(session)}
    ${renderRechooseBanner(session)}
    ${showIntroBlocks ? renderIntro(session) : ''}
    ${
      showIntroBlocks && heroUrl
        ? `<div class="gifting-ambassador__hero">${renderGiftingImage({
            url: heroUrl,
            alt: heroAsset?.title || session.campaign?.name || 'Campaign',
            width: 800,
            height: 350,
            eager: true,
          })}</div>`
        : ''
    }
    ${event ? renderEventMeta(session) : ''}
    ${showIntroBlocks && briefHtml ? `<div class="gifting-ambassador__brief rte">${sanitizeHtml(briefHtml)}</div>` : ''}
    ${renderSelectionCountHtml(session)}
    ${renderSelectedSummaryHtml(session)}
    <p class="gifting-ambassador__section-label">${escapeHtml(sectionLabel)} <span class="req">*</span></p>
    ${productCatalog}
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-next>Continue to details →</button>
    </div>
  `;
}

/**
 * Premium collection-page style product step for event campaigns: full-bleed hero,
 * event meta, and a flush (gapless) product grid — no drop accordions.
 * @param {GiftingSession} session @param {HTMLElement} container @param {GiftingProduct[]} products
 */
function renderEventProductsStep(session, container, products) {
  const flatProducts = [...products].sort(compareBySortOrder);
  const productGrid = flatProducts.length
    ? `<div class="gifting-ambassador__product-grid gifting-ambassador__product-grid--collection" data-gifting-product-grid>${renderProductCells(
        flatProducts,
        session,
        renderEventProductCard
      )}</div>`
    : '';

  container.innerHTML = `
    ${renderEventHero(session, {
      title: 'Select your outfit',
      subtitle: '',
    })}
    ${
      renderRechooseBanner(session) || renderEventMeta(session)
        ? `<div class="gifting-ambassador__event-inner">
            ${renderRechooseBanner(session)}
            ${renderEventMeta(session)}
          </div>`
        : ''
    }
    ${renderGiftingDebugPanel(session)}
    ${renderEventSelectionTray(session)}
    ${productGrid}
    <div class="gifting-ambassador__event-inner">
      <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
      <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
        <button type="button" class="gifting-ambassador__btn-primary" data-gifting-next>Continue to details →</button>
      </div>
    </div>
  `;
}

/** @param {HTMLElement} cardEl @param {GiftingProduct} product */
function updateProductCardSelectionState(cardEl, product) {
  const productKey = getProductKey(product);
  const selection = selections[productKey];
  const isSelected = isActiveSelection(selection);

  cardEl.classList.toggle('is-selected', isSelected);

  cardEl.querySelectorAll('[data-size-id]').forEach((chip) => {
    if (!(chip instanceof HTMLButtonElement)) return;
    const selected = isSelected && Number(selection?.sizeId) === Number(chip.dataset.sizeId);
    chip.classList.toggle('is-selected', selected);
  });

  // Keep the mobile size-trigger in sync ("Select size +" ⇄ "Size M · Change").
  const trigger = cardEl.querySelector('[data-gifting-size-trigger]');
  if (trigger instanceof HTMLButtonElement && !trigger.disabled) {
    if (isSelected) {
      const triggerSizeName = (product.sizes || []).find(
        (s) => Number(s.sizeId) === Number(selection?.sizeId)
      )?.name;
      trigger.innerHTML = `<span class="gifting-ambassador__size-trigger-label">Size ${escapeHtml(
        triggerSizeName || ''
      )}</span><span class="gifting-ambassador__size-trigger-change">Change</span>`;
      trigger.classList.add('is-selected');
    } else {
      trigger.innerHTML =
        '<span class="gifting-ambassador__size-trigger-label">Select size</span><span class="gifting-ambassador__size-trigger-icon" aria-hidden="true">+</span>';
      trigger.classList.remove('is-selected');
    }
  }

  const info = cardEl.querySelector('.gifting-ambassador__product-info');
  if (!(info instanceof HTMLElement)) return;

  const badge = info.querySelector('.gifting-ambassador__added-badge');
  if (isSelected) {
    const sizeName = (product.sizes || []).find(
      (s) => Number(s.sizeId) === Number(selection?.sizeId)
    )?.name;
    const badgeHtml = `✓ Added${sizeName ? ` · Size ${escapeHtml(sizeName)}` : ''}`;
    if (badge instanceof HTMLElement) {
      badge.innerHTML = badgeHtml;
    } else {
      info.insertAdjacentHTML('beforeend', `<div class="gifting-ambassador__added-badge">${badgeHtml}</div>`);
    }
  } else if (badge instanceof HTMLElement) {
    badge.remove();
  }
}

/** @param {HTMLElement} container @param {string} selector @param {string} html @param {string} insertBeforeSelector */
function replaceProductsStepBlock(container, selector, html, insertBeforeSelector) {
  const existing = container.querySelector(selector);
  if (html) {
    if (existing instanceof HTMLElement) {
      existing.outerHTML = html;
    } else {
      const anchor = container.querySelector(insertBeforeSelector);
      anchor?.insertAdjacentHTML('beforebegin', html);
    }
    return;
  }

  if (existing instanceof HTMLElement) {
    existing.remove();
  }
}

/** @param {GiftingSession} session */
function updateProductsStepSelectionUi(session) {
  const container = document.querySelector('[data-gifting-step="products"]');
  if (!(container instanceof HTMLElement)) return;

  replaceProductsStepBlock(
    container,
    '[data-gifting-selection-count]',
    renderSelectionCountHtml(session),
    '.gifting-ambassador__section-label'
  );
  replaceProductsStepBlock(
    container,
    '[data-gifting-selected-summary]',
    renderSelectedSummaryHtml(session),
    '.gifting-ambassador__section-label'
  );
  // Event campaigns: refresh the consolidated sticky tray (status + progress + picks).
  replaceProductsStepBlock(
    container,
    '[data-gifting-selection-tray]',
    renderEventSelectionTray(session),
    '.gifting-ambassador__product-grid'
  );

  container.querySelectorAll('.gifting-ambassador__product-card[data-line-id]').forEach((cardEl) => {
    if (!(cardEl instanceof HTMLElement)) return;
    const lineId = Number(cardEl.dataset.lineId);
    const product = session.products.find((item) => item.lineId === lineId);
    if (!product) return;
    updateProductCardSelectionState(cardEl, product);
  });

  // Keep set cards' completeness state/hint in sync as members are picked.
  container.querySelectorAll('.gifting-ambassador__set[data-set-id]').forEach((setEl) => {
    if (!(setEl instanceof HTMLElement)) return;
    const setId = Number(setEl.dataset.setId);
    const set = getSetGroups(session).find((group) => group.setId === setId);
    if (!set) return;
    const status = getSetStatus(set);
    setEl.classList.toggle('is-complete', status === 'complete');
    setEl.classList.toggle('is-partial', status === 'partial');
    const hint = setEl.querySelector('[data-gifting-set-hint]');
    if (hint instanceof HTMLElement) {
      hint.textContent = getSetHintText(status);
    }
  });
}

/**
 * Toggle a size selection for a product. Shared by the inline size strip (desktop) and the mobile
 * bottom-sheet so both paths enforce identical locked/selectable guards, the outfit-max cap, and
 * error/toast/UI sync.
 * @param {GiftingSession} session @param {number} lineId @param {number} sizeId
 * @returns {{ ok: boolean, isDeselect: boolean }}
 */
function applySizeSelection(session, lineId, sizeId) {
  if (!Number.isFinite(lineId) || !Number.isFinite(sizeId) || sizeId <= 0) {
    return { ok: false, isDeselect: false };
  }
  const product = session.products.find((item) => item.lineId === lineId);
  if (!product || isProductLocked(product)) return { ok: false, isDeselect: false };

  const sizeMeta = (product.sizes || []).find((s) => Number(s.sizeId) === sizeId);
  if (!isSizeSelectable(sizeMeta, product)) return { ok: false, isDeselect: false };

  const { outfit, max } = getSelectionLimits(session);
  const productKey = getProductKey(product);
  const existing = selections[productKey];
  const currentlySelected = isActiveSelection(existing);
  const isDeselect = currentlySelected && Number(existing?.sizeId) === sizeId;
  const maxQty = getEffectiveMaxQty(product, sizeMeta);

  // Apply tentatively, then enforce the max in the campaign's counting unit. (A partial set
  // member doesn't add an outfit, so this only blocks genuine overflow.)
  const previous = existing;
  if (isDeselect) {
    delete selections[productKey];
  } else {
    if (maxQty < 1) {
      const limitMsg =
        getProductQtyLimit(product) === 0
          ? 'This product has a limit of 0 and cannot be selected.'
          : 'This size is at its product/stock limit.';
      setStepError(limitMsg);
      showGiftingToast(limitMsg);
      return { ok: false, isDeselect: false };
    }
    selections[productKey] = {
      lineId: product.lineId,
      productId: product.productId,
      scheduleItemId: product.scheduleItemId,
      isPendingProduct: product.isPendingProduct,
      sizeId,
      // Cap at product limit even when event stock remaining is higher.
      qty: Math.min(1, maxQty),
    };
  }

  if (!isDeselect && max > 0 && getOccupiedOutfitSlots(session) > max) {
    if (previous) selections[productKey] = previous;
    else delete selections[productKey];
    const unit = outfit ? 'outfit' : 'item';
    const blockMsg =
      max === 1
        ? `You can only choose 1 ${unit}. Deselect your current ${unit} first to choose a different one.`
        : `You've reached the maximum of ${max} ${unit}s. Deselect one first to choose another.`;
    setStepError(blockMsg);
    showGiftingToast(blockMsg);
    return { ok: false, isDeselect };
  }

  setStepError('');
  showGiftingToast(buildSelectionToast(session, product, sizeMeta, isDeselect));
  try {
    updateProductsStepSelectionUi(session);
  } catch (error) {
    console.error('[gifting] selection UI update failed', error);
    renderProductsStep(session);
    bindStepEvents(session);
  }
  return { ok: true, isDeselect };
}

/**
 * Lazily builds the singleton mobile bottom-sheet size picker (one <dialog> reused across cards).
 * @returns {HTMLDialogElement | null} null when <dialog> is unsupported (inline strip stays usable).
 */
function ensureGiftingSizeSheet() {
  const existing = document.getElementById('gifting-size-sheet');
  if (existing instanceof HTMLDialogElement) return existing;
  if (typeof HTMLDialogElement === 'undefined') return null;

  const sheet = document.createElement('dialog');
  sheet.id = 'gifting-size-sheet';
  sheet.className = 'gifting-ambassador gifting-size-sheet';
  sheet.innerHTML = '<div class="gifting-size-sheet__inner" data-gifting-sheet-inner></div>';
  document.body.appendChild(sheet);

  sheet.addEventListener('click', (event) => {
    const node = event.target;
    if (node === sheet) {
      closeGiftingSizeSheet();
      return;
    }
    if (!(node instanceof Element)) return;
    if (node.closest('[data-gifting-sheet-close]')) {
      closeGiftingSizeSheet();
      return;
    }
    const sizeBtn = node.closest('[data-size-id]');
    if (sizeBtn instanceof HTMLButtonElement && sessionData) {
      const result = applySizeSelection(
        sessionData,
        Number(sizeBtn.dataset.lineId),
        Number(sizeBtn.dataset.sizeId)
      );
      if (result.ok) closeGiftingSizeSheet();
    }
  });

  return sheet;
}

/** Opens the bottom-sheet for a product. @param {GiftingSession} session @param {GiftingProduct} product */
function openGiftingSizeSheet(session, product) {
  const sheet = ensureGiftingSizeSheet();
  if (!sheet) return;
  renderGiftingSizeSheet(session, product);
  if (!sheet.open) sheet.showModal();
}

/** Closes the bottom-sheet if open. */
function closeGiftingSizeSheet() {
  const sheet = document.getElementById('gifting-size-sheet');
  if (sheet instanceof HTMLDialogElement && sheet.open) sheet.close();
}

/**
 * Renders the sheet body: product image + name + drop, and the full size range as large chips
 * (selectable active, sold-out struck through, unavailable greyed). Mirrors the card's size logic.
 * @param {GiftingSession} session @param {GiftingProduct} product
 */
function renderGiftingSizeSheet(session, product) {
  const sheet = document.getElementById('gifting-size-sheet');
  const inner = sheet?.querySelector('[data-gifting-sheet-inner]');
  if (!(inner instanceof HTMLElement)) return;

  const selection = selections[getProductKey(product)];
  const selectedSizeId = isActiveSelection(selection) ? Number(selection?.sizeId) : 0;
  const shipFrom = getProductShipFromDisplay(product, session);

  const sizeButtons = (product.sizes || [])
    .map((size) => {
      const soldOut = isSizeSoldOut(size, product);
      const selectable = isSizeSelectable(size, product);
      const selected = selectedSizeId === Number(size.sizeId);
      const unavailableLabel = soldOut ? ' — sold out' : ' — unavailable';
      return `
        <button
          type="button"
          class="gifting-size-sheet__size${selected ? ' is-selected' : ''}${selectable ? '' : ' is-unavailable'}"
          data-line-id="${product.lineId}"
          data-size-id="${size.sizeId}"
          ${selectable ? '' : 'disabled'}
          aria-label="${escapeHtml(size.name)}${selectable ? '' : unavailableLabel}"
        >
          <span class="gifting-size-sheet__size-label">${escapeHtml(size.name)}</span>
        </button>
      `;
    })
    .join('');

  inner.innerHTML = `
    <div class="gifting-size-sheet__head">
      ${
        product.thumbUrl
          ? renderGiftingImage({
              url: product.thumbUrl,
              alt: product.name,
              width: 200,
              height: 267,
              className: 'gifting-size-sheet__thumb',
            })
          : ''
      }
      <div class="gifting-size-sheet__meta">
        <p class="gifting-size-sheet__name">${escapeHtml(product.name)}</p>
        ${product.dropName ? `<p class="gifting-size-sheet__drop">${escapeHtml(product.dropName)}</p>` : ''}
        ${shipFrom ? `<p class="gifting-size-sheet__ship">Ships from ${escapeHtml(shipFrom)}</p>` : ''}
        <p class="gifting-size-sheet__hint">${
          selectedSizeId
            ? 'Tap a size to change · tap the selected size to remove'
            : 'Select your size'
        }</p>
      </div>
      <button type="button" class="gifting-size-sheet__close" data-gifting-sheet-close aria-label="Close">×</button>
    </div>
    <div class="gifting-size-sheet__sizes" role="group" aria-label="Select a size">${sizeButtons}</div>
  `;
}

/** @param {GiftingSession} session */
function renderDetailsStep(session) {
  const container = document.querySelector('[data-gifting-step="details"]');
  if (!(container instanceof HTMLElement)) return;

  const deliverables = getDeliverables(session);
  const deliverableRows = deliverables.length
    ? deliverables
        .map((item) => {
          const checked = formState.deliverableIds.includes(item.id);
          return `
            <label class="gifting-ambassador__check-row${checked ? ' is-checked' : ''}" data-deliverable-id="${item.id}">
              <span class="gifting-ambassador__check-box${checked ? ' is-checked' : ''}">${checked ? '✓' : ''}</span>
              <span>${escapeHtml(item.label)}${item.required ? ' <span class="req">*</span>' : ''}</span>
            </label>
          `;
        })
        .join('')
    : '';

  const event = isEventCampaign(session);
  // Instagram/TikTok handle is required for influencer campaigns, optional for events.
  const igHandleRequired = !event && (isCodeAccessMode() || (isTokenAccessMode() && !igHandleLocked));
  const igHandleReadonly = isReadOnly || igHandleLocked;

  container.innerHTML = `
    <h2 class="gifting-ambassador__form-section-title">Your details</h2>
    <div class="gifting-ambassador__form-grid">
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-first-name">First name</label>
        <input class="gifting-ambassador__input" id="gifting-first-name" data-field="firstName" value="${escapeHtml(formState.firstName)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-last-name">Last name</label>
        <input class="gifting-ambassador__input" id="gifting-last-name" data-field="lastName" value="${escapeHtml(formState.lastName)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-email">Email</label>
      <input class="gifting-ambassador__input" id="gifting-email" type="email" data-field="email" value="${escapeHtml(formState.email)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label${igHandleRequired ? ' req' : ''}" for="gifting-ig-handle">Instagram or TikTok handle</label>
      <input class="gifting-ambassador__input" id="gifting-ig-handle" type="text" data-field="igHandle" autocapitalize="off" autocomplete="username" placeholder="@instagram or @tiktok" value="${escapeHtml(formState.igHandle)}" ${igHandleReadonly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-phone">Phone number</label>
      <input class="gifting-ambassador__input" id="gifting-phone" type="tel" data-field="phone" value="${escapeHtml(formState.phone)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-address">Shipping address</label>
      <input class="gifting-ambassador__input" id="gifting-address" data-field="addressLine1" value="${escapeHtml(formState.addressLine1)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    <div class="gifting-ambassador__form-grid">
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label" for="gifting-city">City</label>
        <input class="gifting-ambassador__input" id="gifting-city" data-field="city" value="${escapeHtml(formState.city)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
      <div class="gifting-ambassador__field">
        <label class="gifting-ambassador__field-label req" for="gifting-postcode">Postcode</label>
        <input class="gifting-ambassador__input" id="gifting-postcode" data-field="postcode" value="${escapeHtml(formState.postcode)}" ${isReadOnly ? 'readonly' : ''}>
      </div>
    </div>
    <div class="gifting-ambassador__field">
      <label class="gifting-ambassador__field-label req" for="gifting-country">Country</label>
      <input class="gifting-ambassador__input" id="gifting-country" data-field="country" value="${escapeHtml(formState.country)}" ${isReadOnly ? 'readonly' : ''}>
    </div>
    ${
      event
        ? `
          <div class="gifting-ambassador__field">
            <label class="gifting-ambassador__field-label" for="gifting-dietary">Dietary requirements</label>
            <textarea class="gifting-ambassador__textarea" id="gifting-dietary" data-field="dietaryRequirements" placeholder="Let us know about any allergies or dietary needs…" ${isReadOnly ? 'readonly' : ''}>${escapeHtml(formState.dietaryRequirements)}</textarea>
          </div>
        `
        : ''
    }
    ${
      deliverableRows
        ? `
          <div class="gifting-ambassador__form-section">
            <h3 class="gifting-ambassador__form-section-title">Content deliverables <span class="req">*</span></h3>
            <p class="gifting-ambassador__form-section-lead">Please confirm which content deliverables you would like to create</p>
            ${deliverableRows}
          </div>
        `
        : ''
    }
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-next>${event ? 'Continue to confirm →' : 'Continue to terms →'}</button>
      <button type="button" class="gifting-ambassador__btn-secondary" data-gifting-back>← Back to ${event ? 'outfit picks' : 'product picks'}</button>
    </div>
  `;
}

/** @param {GiftingSession} session */
function renderTermsStep(session) {
  const container = document.querySelector('[data-gifting-step="terms"]');
  if (!(container instanceof HTMLElement)) return;

  if (isEventCampaign(session)) {
    renderEventTermsStep(session, container);
    return;
  }

  const deadline = session.campaign?.contentDeadline || '';
  const deadlineNote = session.campaign?.contentDeadlineNote || '';
  const agreementHtml = String(session.campaign?.agreementHtml || session.campaign?.termsHtml || '').trim();
  const confirmChecked = formState.deadlineChoice === 'confirm';
  const needTimeChecked = formState.deadlineChoice === 'need_time';

  container.innerHTML = `
    <h2 class="gifting-ambassador__form-section-title">Content deadline + terms</h2>
    ${renderSubmitSummaryHtml(session)}
    ${
      deadline
        ? `
          <div class="gifting-ambassador__deadline-box">
            <p class="gifting-ambassador__deadline-title">Content due ${escapeHtml(deadline)}</p>
            <p class="gifting-ambassador__deadline-sub">${escapeHtml(deadlineNote || 'We require content posted by this date. Let us know if you need additional time.')}</p>
          </div>
          <p class="gifting-ambassador__field-label req">Can you meet this deadline?</p>
          <button type="button" class="gifting-ambassador__radio-row${confirmChecked ? ' is-checked' : ''}" data-deadline-choice="confirm">
            <span class="gifting-ambassador__radio-dot${confirmChecked ? ' is-checked' : ''}">${confirmChecked ? '<span class="gifting-ambassador__radio-inner"></span>' : ''}</span>
            <span>I confirm I can post my content on ${escapeHtml(deadline)} as part of the campaign launch.</span>
          </button>
          <button type="button" class="gifting-ambassador__radio-row${needTimeChecked ? ' is-checked' : ''}" data-deadline-choice="need_time">
            <span class="gifting-ambassador__radio-dot${needTimeChecked ? ' is-checked' : ''}">${needTimeChecked ? '<span class="gifting-ambassador__radio-inner"></span>' : ''}</span>
            <span>I require additional time — I will send a proposed date via DM or email.</span>
          </button>
        `
        : ''
    }
    <div class="gifting-ambassador__form-section">
      <label class="gifting-ambassador__field-label" for="gifting-notes">Notes (optional)</label>
      <textarea class="gifting-ambassador__textarea" id="gifting-notes" data-field="notes" placeholder="Ivory colourway if possible…">${escapeHtml(formState.notes)}</textarea>
    </div>
    <div class="gifting-ambassador__form-section">
      <h3 class="gifting-ambassador__form-section-title">Agreement &amp; submission</h3>
      ${
        agreementHtml
          ? `<div class="gifting-ambassador__tc-box">${sanitizeHtml(agreementHtml)}</div>`
          : `
            <div class="gifting-ambassador__tc-box">
              <p>By submitting this form you agree to:</p>
              <ul>
                <li>Deliver your selected deliverables by the agreed date</li>
                <li>Post content on the agreed date</li>
                <li>Grant SABO full usage rights to your content</li>
              </ul>
              <p>We're so excited to collaborate and can't wait to see your creative direction!</p>
            </div>
          `
      }
      <label class="gifting-ambassador__consent-row${formState.termsAccepted ? ' is-checked' : ''}">
        <span class="gifting-ambassador__check-box${formState.termsAccepted ? ' is-checked' : ''}" data-consent="terms">${formState.termsAccepted ? '✓' : ''}</span>
        <span>I agree to the terms and deliverables of this collaboration.</span>
      </label>
      <label class="gifting-ambassador__consent-row${formState.dataConsentAccepted ? ' is-checked' : ''}">
        <span class="gifting-ambassador__check-box${formState.dataConsentAccepted ? ' is-checked' : ''}" data-consent="data">${formState.dataConsentAccepted ? '✓' : ''}</span>
        <span>I consent to SABO Skirt storing my personal details and shipping address for fulfilment purposes.</span>
      </label>
    </div>
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-submit ${session.canSubmit ? '' : 'disabled'}>${escapeHtml(getSubmitLabel(session))}</button>
      <button type="button" class="gifting-ambassador__btn-secondary" data-gifting-back>← Back to details</button>
    </div>
  `;
}

/** Event confirmation step: content waiver (optional) + data consent. @param {GiftingSession} session @param {HTMLElement} container */
function renderEventTermsStep(session, container) {
  const waiverHtml = String(session.campaign?.contentWaiverHtml || '').trim();
  const rsvpClosed = isRsvpClosed(session);
  const submitDisabled = !session.canSubmit || rsvpClosed;

  container.innerHTML = `
    <h2 class="gifting-ambassador__form-section-title">Confirm your RSVP</h2>
    ${renderEventMeta(session)}
    ${renderSubmitSummaryHtml(session)}
    ${
      waiverHtml
        ? `
          <div class="gifting-ambassador__form-section">
            <h3 class="gifting-ambassador__form-section-title">Content waiver</h3>
            <div class="gifting-ambassador__tc-box">${sanitizeHtml(waiverHtml)}</div>
            <label class="gifting-ambassador__consent-row${formState.contentWaiverAccepted ? ' is-checked' : ''}">
              <span class="gifting-ambassador__check-box${formState.contentWaiverAccepted ? ' is-checked' : ''}" data-consent="waiver">${formState.contentWaiverAccepted ? '✓' : ''}</span>
              <span>I agree to the content waiver above. <span class="req">*</span></span>
            </label>
          </div>
        `
        : ''
    }
    <div class="gifting-ambassador__form-section">
      <label class="gifting-ambassador__consent-row${formState.dataConsentAccepted ? ' is-checked' : ''}">
        <span class="gifting-ambassador__check-box${formState.dataConsentAccepted ? ' is-checked' : ''}" data-consent="data">${formState.dataConsentAccepted ? '✓' : ''}</span>
        <span>I consent to SABO Skirt storing my personal details and shipping address for fulfilment purposes.</span>
      </label>
    </div>
    ${rsvpClosed ? '<p class="gifting-ambassador__inline-error">RSVPs for this event have closed.</p>' : ''}
    <p class="gifting-ambassador__inline-error" data-gifting-step-error hidden></p>
    <div class="gifting-ambassador__actions gifting-ambassador__actions--sticky">
      <button type="button" class="gifting-ambassador__btn-primary" data-gifting-submit ${submitDisabled ? 'disabled' : ''}>${escapeHtml(getSubmitLabel(session))}</button>
      <button type="button" class="gifting-ambassador__btn-secondary" data-gifting-back>← Back to details</button>
    </div>
  `;
}

/** @param {GiftingSession} session */
function showStep(session) {
  const steps = getActiveSteps(session);

  steps.forEach((step, index) => {
    const el = document.querySelector(`[data-gifting-step="${step}"]`);
    if (el instanceof HTMLElement) {
      el.hidden = index !== currentStepIndex;
    }
  });

  ['rsvp', 'products', 'details', 'terms', 'decline'].forEach((step) => {
    if (steps.includes(step)) return;
    const el = document.querySelector(`[data-gifting-step="${step}"]`);
    if (el instanceof HTMLElement) el.hidden = true;
  });

  renderProgress(session);

  const step = steps[currentStepIndex];
  if (step === 'rsvp') renderRsvpStep(session);
  if (step === 'products') renderProductsStep(session);
  if (step === 'details') renderDetailsStep(session);
  if (step === 'terms') renderTermsStep(session);
  if (step === 'decline') renderDeclineStep(session);

  bindStepEvents(session);
}

/** @param {GiftingSession} session */
function bindStepEvents(session) {
  const steps = getActiveSteps(session);
  const activeStep = document.querySelector(`[data-gifting-step="${steps[currentStepIndex]}"]`);
  if (!(activeStep instanceof HTMLElement)) return;

  activeStep.querySelectorAll('[data-field]').forEach((input) => {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    if (input.dataset.bound) return;
    input.dataset.bound = 'true';
    input.addEventListener('input', () => {
      const field = input.dataset.field;
      if (!field) return;
      formState[field] = input.value;
    });
  });

  const productStep = document.querySelector('[data-gifting-step="products"]');
  if (productStep instanceof HTMLElement && productStep.dataset.eventsBound !== 'true') {
    productStep.dataset.eventsBound = 'true';
    productStep.addEventListener('click', (event) => {
      const session = sessionData;
      if (!session) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const loadMore = target.closest('[data-gifting-load-more]');
      if (loadMore instanceof HTMLButtonElement) {
        const dropKey = loadMore.dataset.dropKey || '';
        if (!dropKey) return;
        visibleCountByDrop[dropKey] =
          (visibleCountByDrop[dropKey] ?? GIFTING_PRODUCTS_PAGE_SIZE) + GIFTING_PRODUCTS_PAGE_SIZE;
        renderProductsStep(session);
        bindStepEvents(session);
        return;
      }

      const dropToggle = target.closest('[data-gifting-drop-toggle]');
      if (dropToggle instanceof HTMLButtonElement) {
        const dropKey = dropToggle.dataset.dropKey || '';
        if (!dropKey) return;
        collapsedDrops[dropKey] = !collapsedDrops[dropKey];
        const section = dropToggle.closest('.gifting-ambassador__drop');
        if (section instanceof HTMLElement) {
          const collapsed = Boolean(collapsedDrops[dropKey]);
          section.classList.toggle('is-collapsed', collapsed);
          dropToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        }
        return;
      }

      // Continue action surfaced in the always-visible tray so the user need not scroll down.
      const trayContinue = target.closest('[data-gifting-tray-continue]');
      if (trayContinue instanceof HTMLButtonElement && !isReadOnly) {
        if (!validateStep(session)) return;
        currentStepIndex = Math.min(currentStepIndex + 1, getActiveSteps(session).length - 1);
        showStep(session);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Remove control in the sticky "Your selections" summary — clears a pick so the
      // influencer can choose a different product (the key "how do I swap?" affordance).
      const removeBtn = target.closest('[data-gifting-remove-selection]');
      if (removeBtn instanceof HTMLButtonElement && !isReadOnly) {
        const removeLineId = Number(removeBtn.dataset.lineId);
        if (!Number.isFinite(removeLineId)) return;
        const removeKey = String(removeLineId);
        const removed = session.products.find((item) => item.lineId === removeLineId);
        if (removed && isProductLocked(removed)) return;
        delete selections[removeKey];
        setStepError('');
        showGiftingToast(`${removed?.name || 'Item'} removed — pick another to swap it in.`);
        try {
          updateProductsStepSelectionUi(session);
        } catch (error) {
          console.error('[gifting] selection UI update failed', error);
          renderProductsStep(session);
          bindStepEvents(session);
        }
        return;
      }

      // Mobile: a size trigger opens the bottom-sheet picker (full-width tap targets + product
      // image/name) rather than cramming the size run under a half-width card.
      const sizeTrigger = target.closest('[data-gifting-size-trigger]');
      if (sizeTrigger instanceof HTMLButtonElement && !isReadOnly) {
        const triggerProduct = session.products.find(
          (item) => item.lineId === Number(sizeTrigger.dataset.lineId)
        );
        if (triggerProduct && !isProductLocked(triggerProduct)) {
          openGiftingSizeSheet(session, triggerProduct);
        }
        return;
      }

      const chip = target.closest('[data-size-id]');
      if (!(chip instanceof HTMLButtonElement) || isReadOnly) return;
      applySizeSelection(session, Number(chip.dataset.lineId), Number(chip.dataset.sizeId));
    });
  }

  activeStep.querySelectorAll('[data-deliverable-id]').forEach((row) => {
    if (!(row instanceof HTMLElement) || row.dataset.bound) return;
    row.dataset.bound = 'true';
    row.addEventListener('click', () => {
      const id = Number(row.getAttribute('data-deliverable-id'));
      if (!id) return;

      if (formState.deliverableIds.includes(id)) {
        formState.deliverableIds = formState.deliverableIds.filter((value) => value !== id);
      } else {
        formState.deliverableIds = [...formState.deliverableIds, id];
      }

      renderDetailsStep(session);
      bindStepEvents(session);
    });
  });

  activeStep.querySelectorAll('[data-gifting-rsvp]').forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement) || btn.dataset.bound) return;
    btn.dataset.bound = 'true';
    btn.addEventListener('click', () => {
      formState.attendance = btn.dataset.giftingRsvp === 'notattending' ? 'NotAttending' : 'Attending';
      // Move off the RSVP landing to the next step in the (recomputed) flow.
      currentStepIndex = 1;
      showStep(session);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });

  activeStep.querySelectorAll('[data-deadline-choice]').forEach((row) => {
    if (!(row instanceof HTMLButtonElement) || row.dataset.bound) return;
    row.dataset.bound = 'true';
    row.addEventListener('click', () => {
      formState.deadlineChoice = row.dataset.deadlineChoice === 'need_time' ? 'need_time' : 'confirm';
      renderTermsStep(session);
      bindStepEvents(session);
    });
  });

  activeStep.querySelectorAll('[data-consent]').forEach((box) => {
    const row = box.closest('.gifting-ambassador__consent-row');
    if (!(row instanceof HTMLElement) || row.dataset.bound) return;
    row.dataset.bound = 'true';
    row.addEventListener('click', () => {
      const type = box.getAttribute('data-consent');
      if (type === 'terms') formState.termsAccepted = !formState.termsAccepted;
      if (type === 'data') formState.dataConsentAccepted = !formState.dataConsentAccepted;
      if (type === 'waiver') formState.contentWaiverAccepted = !formState.contentWaiverAccepted;
      renderTermsStep(session);
      bindStepEvents(session);
    });
  });

  const nextBtn = activeStep.querySelector('[data-gifting-next]');
  if (nextBtn instanceof HTMLButtonElement && !nextBtn.dataset.bound) {
    nextBtn.dataset.bound = 'true';
    nextBtn.addEventListener('click', () => {
      if (!validateStep(session)) return;
      currentStepIndex = Math.min(currentStepIndex + 1, getActiveSteps(session).length - 1);
      showStep(session);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  const backBtn = activeStep.querySelector('[data-gifting-back]');
  if (backBtn instanceof HTMLButtonElement && !backBtn.dataset.bound) {
    backBtn.dataset.bound = 'true';
    backBtn.addEventListener('click', () => {
      syncFieldsFromDom(activeStep);
      currentStepIndex = Math.max(0, currentStepIndex - 1);
      showStep(session);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  const submitBtn = activeStep.querySelector('[data-gifting-submit]');
  if (submitBtn instanceof HTMLButtonElement && !submitBtn.dataset.bound) {
    submitBtn.dataset.bound = 'true';
    submitBtn.addEventListener('click', () => submitPreferences(session));
  }
}

/** @param {HTMLElement} scope */
function syncFieldsFromDom(scope) {
  scope.querySelectorAll('[data-field]').forEach((input) => {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    const field = input.dataset.field;
    if (!field) return;
    formState[field] = input.value;
  });
}

/** @param {string} message */
function setStepError(message) {
  const steps = getActiveSteps(sessionData);
  const activeStep = document.querySelector(`[data-gifting-step="${steps[currentStepIndex]}"]`);
  const errorEl = activeStep?.querySelector('[data-gifting-step-error]');
  if (!(errorEl instanceof HTMLElement)) return;

  if (!message) {
    errorEl.hidden = true;
    errorEl.textContent = '';
    return;
  }

  errorEl.hidden = false;
  errorEl.textContent = message;
}

let giftingToastTimer = 0;
/**
 * Transient, viewport-fixed feedback so a blocked action is visible even when the user is
 * scrolled down in the product grid (the inline step error sits at the top/bottom, off-screen).
 * @param {string} message
 */
function showGiftingToast(message) {
  if (!message) return;
  let toast = document.querySelector('[data-gifting-toast]');
  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement('div');
    toast.className = 'gifting-ambassador__toast';
    toast.setAttribute('data-gifting-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'assertive');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  // Force reflow so re-triggering the same toast replays the transition.
  void toast.offsetWidth;
  toast.classList.add('is-visible');
  window.clearTimeout(giftingToastTimer);
  giftingToastTimer = window.setTimeout(() => {
    toast.classList.remove('is-visible');
  }, 3400);
}

/**
 * Validates the product selection (min/max/exact outfit counts). Sets are optional pairs, so a
 * half-picked set is allowed. Extracted so it runs both when leaving the products step AND again
 * at submit — the submit button lives on a later step.
 * @param {GiftingSession} session
 * @returns {boolean}
 */
function validateProductSelection(session) {
  const { outfit, min, max } = getSelectionLimits(session);
  const count = getSelectionCount(session);
  const unit = outfit ? 'outfit' : 'item';

  // Sets are optional pairs — one piece is enough to submit, so a half-picked set is valid.

  if (count === 0) {
    setStepError('Please select at least one piece and size to continue.');
    return false;
  }

  if (min > 0 && count < min) {
    setStepError(`Please select at least ${min} ${unit}${min === 1 ? '' : 's'}.`);
    return false;
  }

  if (max > 0 && count > max) {
    setStepError(`You can select up to ${max} ${unit}${max === 1 ? '' : 's'}.`);
    return false;
  }

  // Per-line product limit / event-stock ceiling — reject over-qty even if campaign min/max is ok.
  for (const entry of Object.values(selections)) {
    if (!isActiveSelection(entry)) continue;
    const product = findProductByLineId(session, entry.lineId);
    const sizeMeta = (product?.sizes || []).find((s) => Number(s.sizeId) === Number(entry.sizeId));
    const maxQty = getEffectiveMaxQty(product, sizeMeta);
    if (Number(entry.qty) > maxQty) {
      const remaining = getProductRemainingQty(product);
      const lineLimit = getProductQtyLimit(product);
      if (remaining != null && remaining <= 0) {
        setStepError(`“${product?.name || 'Item'}” is no longer available.`);
      } else if (remaining != null && Number(entry.qty) > remaining) {
        setStepError(`“${product?.name || 'Item'}” only has ${remaining} left.`);
      } else if (lineLimit != null) {
        setStepError(`“${product?.name || 'Item'}” is limited to ${lineLimit} per selection.`);
      } else {
        setStepError(`“${product?.name || 'Item'}” exceeds available stock for the selected size.`);
      }
      return false;
    }
  }

  setStepError('');
  return true;
}

/** @param {GiftingSession} session */
function validateStep(session) {
  const steps = getActiveSteps(session);
  const step = steps[currentStepIndex];

  if (step === 'products') {
    return validateProductSelection(session);
  }

  if (step === 'details') {
    const event = isEventCampaign(session);
    const activeStep = document.querySelector('[data-gifting-step="details"]');
    if (activeStep instanceof HTMLElement) syncFieldsFromDom(activeStep);

    const required = [
      ['firstName', 'First name'],
      ['lastName', 'Last name'],
      ['email', 'Email'],
      ['phone', 'Phone number'],
      ['addressLine1', 'Shipping address'],
      ['postcode', 'Postcode'],
      ['country', 'Country'],
    ];

    // Instagram/TikTok handle is only required for influencer campaigns.
    if (!event && (isCodeAccessMode() || (isTokenAccessMode() && !igHandleLocked))) {
      required.splice(3, 0, ['igHandle', 'Instagram or TikTok handle']);
    }

    for (const [field, label] of required) {
      if (!String(formState[field] || '').trim()) {
        setStepError(`${label} is required.`);
        return false;
      }
    }

    // Events don't use content deliverables.
    if (!event) {
      const deliverables = getDeliverables(session);
      if (deliverables.length && formState.deliverableIds.length === 0) {
        setStepError('Please select at least one content deliverable.');
        return false;
      }

      const missingRequired = deliverables
        .filter((item) => item.required)
        .filter((item) => !formState.deliverableIds.includes(item.id));

      if (missingRequired.length) {
        setStepError(`Please select: ${missingRequired.map((item) => item.label).join(', ')}.`);
        return false;
      }
    }

    setStepError('');
    return true;
  }

  if (step === 'terms') {
    const activeStep = document.querySelector('[data-gifting-step="terms"]');
    if (activeStep instanceof HTMLElement) syncFieldsFromDom(activeStep);

    if (isEventCampaign(session)) {
      if (isRsvpClosed(session)) {
        setStepError('RSVPs for this event have closed.');
        return false;
      }

      if (String(session.campaign?.contentWaiverHtml || '').trim() && !formState.contentWaiverAccepted) {
        setStepError('Please agree to the content waiver.');
        return false;
      }

      if (!formState.dataConsentAccepted) {
        setStepError('Please consent to storing your details for fulfilment.');
        return false;
      }

      setStepError('');
      return true;
    }

    if (session.campaign.contentDeadline && !formState.deadlineChoice) {
      setStepError('Please confirm whether you can meet the content deadline.');
      return false;
    }

    if (!formState.termsAccepted) {
      setStepError('Please agree to the collaboration terms.');
      return false;
    }

    if (!formState.dataConsentAccepted) {
      setStepError('Please consent to storing your details for fulfilment.');
      return false;
    }

    setStepError('');
    return true;
  }

  if (step === 'decline') {
    const activeStep = document.querySelector('[data-gifting-step="decline"]');
    if (activeStep instanceof HTMLElement) syncFieldsFromDom(activeStep);

    if (isRsvpClosed(session)) {
      setStepError('RSVPs for this event have closed.');
      return false;
    }

    for (const [field, label] of [
      ['firstName', 'First name'],
      ['lastName', 'Last name'],
      ['email', 'Email'],
    ]) {
      if (!String(formState[field] || '').trim()) {
        setStepError(`${label} is required.`);
        return false;
      }
    }

    setStepError('');
    return true;
  }

  return true;
}

/** Attach the access credential (token or campaign code) to a submit payload. @param {Record<string, unknown>} payload */
function attachSubmitCredential(payload) {
  if (inviteToken) {
    payload.Token = inviteToken;
  } else if (campaignCode) {
    payload.CampaignCode = campaignCode;
  }
}

/** Attach contact + shipping identity fields to a submit payload. @param {Record<string, unknown>} payload */
function attachIdentityFields(payload) {
  payload.FirstName = formState.firstName.trim();
  payload.LastName = formState.lastName.trim();
  payload.Email = formState.email.trim();
  payload.Phone = formState.phone.trim();
  payload.AddressLine1 = formState.addressLine1.trim();
  payload.City = formState.city.trim();
  payload.Postcode = formState.postcode.trim();
  payload.Country = formState.country.trim();

  const igHandle = formState.igHandle.trim();
  if (igHandle) {
    payload.IgHandle = igHandle;
  }
}

/** @returns {Array<{ ProductId: number, ScheduleItemId: number, SizeId: number, Qty: number }>} */
function buildSubmitLines() {
  return Object.values(selections)
    .filter((entry) => isActiveSelection(entry))
    .map((entry) => {
      const product = sessionData
        ? findProductByLineId(sessionData, entry.lineId)
        : undefined;
      const sizeMeta = (product?.sizes || []).find((s) => Number(s.sizeId) === Number(entry.sizeId));
      const maxQty = getEffectiveMaxQty(product, sizeMeta);
      const qty = Math.max(1, Math.min(Number(entry.qty) || 1, maxQty > 0 ? maxQty : 1));

      if (entry.isPendingProduct || entry.productId <= 0) {
        return {
          ProductId: 0,
          ScheduleItemId: entry.scheduleItemId,
          SizeId: entry.sizeId,
          Qty: qty,
        };
      }

      return {
        ProductId: entry.productId,
        ScheduleItemId: 0,
        SizeId: entry.sizeId,
        Qty: qty,
      };
    });
}

/** @param {GiftingSession} session */
function buildSubmitPayload(session) {
  // Event — not attending: minimal payload, no product lines or shipping.
  if (isEventCampaign(session) && formState.attendance === 'NotAttending') {
    /** @type {Record<string, unknown>} */
    const declinePayload = {
      AttendanceResponse: 'NotAttending',
      FirstName: formState.firstName.trim(),
      LastName: formState.lastName.trim(),
      Email: formState.email.trim(),
    };
    attachSubmitCredential(declinePayload);
    return declinePayload;
  }

  // Event — attending.
  if (isEventCampaign(session)) {
    /** @type {Record<string, unknown>} */
    const eventPayload = {
      AttendanceResponse: 'Attending',
      DietaryRequirements: formState.dietaryRequirements.trim(),
      ContentWaiverAccepted: formState.contentWaiverAccepted,
      DataConsentAccepted: formState.dataConsentAccepted,
      Lines: buildSubmitLines(),
    };
    attachSubmitCredential(eventPayload);
    attachIdentityFields(eventPayload);
    return eventPayload;
  }

  // Influencer (standard).
  let contentDeadlineResponse = '';
  if (formState.deadlineChoice === 'confirm') {
    contentDeadlineResponse = 'Confirm';
  } else if (formState.deadlineChoice === 'need_time') {
    contentDeadlineResponse = 'NeedsExtension';
  }

  /** @type {Record<string, unknown>} */
  const payload = {
    Notes: formState.notes.trim(),
    Lines: buildSubmitLines(),
    ContentDeadlineResponse: contentDeadlineResponse,
    TermsAccepted: formState.termsAccepted,
    DataConsentAccepted: formState.dataConsentAccepted,
    SelectedDeliverableIds: formState.deliverableIds,
  };

  attachSubmitCredential(payload);

  if (isCodeAccessMode() || isTokenAccessMode() || sessionData?.requiresIdentity) {
    attachIdentityFields(payload);
  }

  return payload;
}

/** @param {GiftingSession} session */
async function submitPreferences(session) {
  if (!validateStep(session)) return;

  // Re-check the selection (sets + counts) at submit — the submit button lives on a later
  // step, so validateStep above only covers that step; this guarantees a half-picked set or
  // out-of-range count can't slip through client-side (the server also enforces it).
  if (!isEventCampaign(session) || formState.attendance !== 'NotAttending') {
    if (!validateProductSelection(session)) return;
  }

  const submitBtn = document.querySelector('[data-gifting-submit]');
  if (!(submitBtn instanceof HTMLButtonElement) || submitBtn.disabled) return;

  submitBtn.setAttribute('aria-busy', 'true');
  submitBtn.disabled = true;

  try {
    const submitQuery = inviteToken ? `?token=${encodeURIComponent(inviteToken)}` : '';
    const response = await fetch(`${getProxyBase()}/submit${submitQuery}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify(buildSubmitPayload(session)),
    });

    if (response.status === 401) {
      throw new Error('Unable to submit preferences. Please open the link from your email.');
    }

    let data = null;

    try {
      data = await response.json();
    } catch (_error) {
      if (response.status === 404) {
        throw new Error('Submit is not available yet. Please check back soon.');
      }
    }

    if (response.status === 404) {
      throw new Error('Submit is not available yet. Please check back soon.');
    }

    const success = Boolean(pick(data, 'Success', 'success'));
    const message = String(pick(data, 'Message', 'message') || '');

    if (!response.ok || !success) {
      throw new Error(message || 'Unable to submit preferences. Please review your selections and try again.');
    }

    clearStoredCampaignCode();
    showSubmitted(session, message);
  } catch (error) {
    setStepError(error instanceof Error ? error.message : 'Unable to submit preferences.');
    submitBtn.removeAttribute('aria-busy');
    submitBtn.disabled = !session.canSubmit;
  }
}

/** @param {GiftingSession} session */
function renderSession(session) {
  sessionData = session;
  initFormState(session);

  const root = getGiftingRoot();
  if (root) root.classList.toggle('gifting-ambassador--event', isEventCampaign(session));

  if (applyReadOnlyStatus(session)) return;

  hideViews(['wizard']);

  currentStepIndex = 0;
  showStep(session);
}

/** @param {string} token @param {{ fromUnlock?: boolean }} [options] */
async function loadInviteByToken(token, options = {}) {
  accessMode = 'token';
  inviteToken = String(token || '').trim();
  clearStoredCampaignCode();

  if (!inviteToken) {
    showUnlock();
    if (options.fromUnlock) {
      setUnlockError('Please enter your campaign code.');
    }
    return;
  }

  if (!options.fromUnlock) {
    showLoading();
  }

  try {
    const data = await fetchSessionByToken(inviteToken);
    handleSessionPayload(data, {
      fromUnlock: options.fromUnlock,
      invalidMessage: 'That invite link is not valid. Check your email and try again.',
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unable to load invite.';

    if (options.fromUnlock) {
      showUnlock();
      setUnlockError(errorMessage);
      return;
    }

    showError(errorMessage, 'Unable to load invite');
  }
}

/** @param {string} code @param {{ fromUnlock?: boolean }} [options] */
async function unlockByCampaignCode(code, options = {}) {
  accessMode = 'code';
  inviteToken = '';
  persistCampaignCode(code);

  if (!campaignCode) {
    showUnlock();
    setUnlockError('Please enter your campaign code.');
    return;
  }

  if (!options.fromUnlock) {
    showLoading();
  }

  try {
    const data = await fetchSessionByCampaignCode(campaignCode);
    handleSessionPayload(data, { fromUnlock: options.fromUnlock });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unable to unlock campaign.';

    if (options.fromUnlock) {
      showUnlock();
      setUnlockError(errorMessage);
      return;
    }

    showError(errorMessage, 'Unable to unlock campaign');
  }
}

function stripCampaignCodeFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('code')) return;

  url.searchParams.delete('code');
  history.replaceState({}, '', url);
}

async function init() {
  if (window.SaboGiftingReCollabMode) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  if ((params.get('exchange') || '').trim() === '1') {
    return;
  }

  const answer = (params.get('answer') || '').trim().toLowerCase();
  if (answer === 'yes' || answer === 'no') {
    return;
  }

  stripCampaignCodeFromUrl();
  inviteToken = resolveInviteToken();

  if (inviteToken) {
    await loadInviteByToken(inviteToken);
    return;
  }

  const previewCode = resolvePreviewCampaignCode();
  if (previewCode) {
    await unlockByCampaignCode(previewCode);
    return;
  }

  const storedCode = readStoredCampaignCode();
  if (storedCode) {
    await unlockByCampaignCode(storedCode);
    return;
  }

  showUnlock();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
